<template>
  <div class="warehouse-app">
    <div class="top-bar">
      <div class="tb-left">
        <div class="logo-icon" @click="openAreaPanel()">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </div>
        <div class="logo-title">智能仓库可视化</div>
      </div>
      <div class="tb-center">
        <select
          v-model="selectedWareId"
          class="ware-select"
          :disabled="wareLoading || wareList.length === 0"
          @change="handleWareChange"
        >
          <option v-if="wareLoading" value="" disabled>加载中...</option>
          <option v-else-if="wareList.length === 0" value="" disabled>
            暂无仓库
          </option>
          <option v-else v-for="w in wareList" :key="w.id" :value="w.id">
            {{ w.wareName }}
          </option>
        </select>
        <button
          class="search-trigger"
          @click="showSearchPanel = !showSearchPanel"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <span>搜索</span>
        </button>
      </div>
      <div class="tb-right">
        <button
          v-if="isMobile"
          class="mobile-search-btn"
          @click="toggleMobileSearch"
        >
          搜索
        </button>
        <button
          class="heatmap-btn"
          :class="{ active: showHeatmap }"
          @click="showHeatmap = !showHeatmap"
        >
          热力图
        </button>
        <template v-if="!isMobile">
          <button
            class="edit-btn"
            :class="{ active: editMode }"
            @click="toggleEdit"
          >
            {{ editMode ? "退出编辑" : "编辑" }}
          </button>
          <button v-if="editMode" class="save-btn" @click="saveToBackend">
            保存
          </button>
        </template>
      </div>
    </div>

    <div class="canvas-area" @click="handleCanvasClick">
      <div
        class="warehouse-viewport"
        ref="warehouseViewport"
        @pointerdown="handleViewportPointerDown"
        @pointermove="handleViewportPointerMove"
        @pointerup="handleViewportPointerUp"
        @pointercancel="handleViewportPointerUp"
      >
        <div
          class="warehouse-stage"
          :style="{ width: stageWidth + 'px', height: stageHeight + 'px' }"
        >
          <div
            ref="warehouseCanvas"
            class="warehouse-canvas"
            :style="{
              width: baseCanvasWidth + 'px',
              height: baseCanvasHeight + 'px',
              transform: 'scale(' + effectiveScale + ')',
              imageRendering: 'pixelated',
            }"
            @dragenter.prevent="handleCanvasDragEnter"
            @dragover="handleCanvasDragOver"
            @drop="handleCanvasDrop"
          >
            <div class="warehouse-ground"></div>
            <div class="guide-layer" v-if="editMode && !isMobile">
              <div
                v-for="(x, i) in guideLines.v"
                :key="'v-' + i + '-' + x"
                class="guide-line guide-line-v"
                :style="{ left: x + 'px', height: baseCanvasHeight + 'px' }"
              ></div>
              <div
                v-for="(y, i) in guideLines.h"
                :key="'h-' + i + '-' + y"
                class="guide-line guide-line-h"
                :style="{ top: y + 'px', width: baseCanvasWidth + 'px' }"
              ></div>
            </div>
            <div class="canvas-stats">
              <span v-if="layoutLoading">加载中...</span>
              <span v-else>已摆放：{{ inventoryData.length }}</span>
            </div>
            <div
              class="canvas-empty"
              v-if="!layoutLoading && inventoryData.length === 0"
            >
              暂无已摆放库位
            </div>
            <div
              v-for="(item, index) in visibleInventoryData"
              :key="index"
              class="location"
              :class="{
                'loc-is-pallet': item.wareType === 2,
                highlight:
                  selectedLocationKey === item.name ||
                  highlightedLocations.has(item.name),
                'batch-selected': selectedBatchItems.includes(item),
                'heatmap-mode':
                  item.wareType === 2 &&
                  !scalingInProgress &&
                  (showHeatmap ||
                    (highlightedLocations.size &&
                      highlightedLocations.has(item.name))),
                overstock:
                  item.wareType === 2 &&
                  !scalingInProgress &&
                  (showHeatmap ||
                    (highlightedLocations.size &&
                      highlightedLocations.has(item.name))) &&
                  item.occupancy > 1,
                [`type-${item.wareType}`]: true,
              }"
              :style="{
                left: item.x + 'px',
                top: item.y + 'px',
                width: item.w + 'px',
                height: item.h + 'px',
                '--loc-font-size': getLocationFontSize(item),
                '--heat-r':
                  item.wareType === 2 &&
                  !scalingInProgress &&
                  (showHeatmap ||
                    (highlightedLocations.size &&
                      highlightedLocations.has(item.name)))
                    ? getHeatColorRGB(item.occupancy).r
                    : '',
                '--heat-g':
                  item.wareType === 2 &&
                  !scalingInProgress &&
                  (showHeatmap ||
                    (highlightedLocations.size &&
                      highlightedLocations.has(item.name)))
                    ? getHeatColorRGB(item.occupancy).g
                    : '',
                '--heat-b':
                  item.wareType === 2 &&
                  !scalingInProgress &&
                  (showHeatmap ||
                    (highlightedLocations.size &&
                      highlightedLocations.has(item.name)))
                    ? getHeatColorRGB(item.occupancy).b
                    : '',
                '--loc-text-color':
                  item.wareType === 2 &&
                  !scalingInProgress &&
                  (showHeatmap || highlightedLocations.has(item.name))
                    ? item.occupancy > 0.65
                      ? '#ffffff'
                      : '#374151'
                    : '',
                transform: 'translate(0,0)',
                ...getPassageBorders(item),
                ...getGateStyle(item),
              }"
              @pointerdown="startDrag($event, item)"
              @click="handleLocationClick(item)"
              @dblclick="showDetail(item)"
              @touchend="handleTouchEnd($event, item)"
            >
              <div
                v-if="editMode || (item.wareType !== 4 && item.wareType !== 5)"
                class="loc-name"
                :class="{ 'vertical-text': item.h > item.w }"
              >
                {{ item.name }}
              </div>
              <div
                v-if="item.wareType === 5"
                class="passage-cover"
                :style="getPassageCoverStyle(item)"
              ></div>
              <div
                v-if="item.wareType === 4"
                class="wall-cover"
                :style="getWallCoverStyle(item)"
              ></div>
              <div
                v-if="
                  editMode &&
                  item.palletWeight !== item.palletHeight &&
                  Math.min(item.w, item.h) >= 40
                "
                class="rotate-btn"
                @click.stop="rotateLocation(item)"
                @pointerdown.stop
                title="旋转方向"
              >
                ↻
              </div>
              <div
                v-if="editMode && item.wareType !== 2"
                class="resize-handle resize-handle-n"
                @pointerdown.stop="startResize($event, item, 'n')"
              ></div>
              <div
                v-if="editMode && item.wareType !== 2"
                class="resize-handle resize-handle-s"
                @pointerdown.stop="startResize($event, item, 's')"
              ></div>
              <div
                v-if="editMode && item.wareType !== 2"
                class="resize-handle resize-handle-e"
                @pointerdown.stop="startResize($event, item, 'e')"
              ></div>
              <div
                v-if="editMode && item.wareType !== 2"
                class="resize-handle resize-handle-w"
                @pointerdown.stop="startResize($event, item, 'w')"
              ></div>
              <div
                v-if="editMode && item.wareType !== 2"
                class="resize-handle resize-handle-nw"
                @pointerdown.stop="startResize($event, item, 'nw')"
              ></div>
              <div
                v-if="editMode && item.wareType !== 2"
                class="resize-handle resize-handle-ne"
                @pointerdown.stop="startResize($event, item, 'ne')"
              ></div>
              <div
                v-if="editMode && item.wareType !== 2"
                class="resize-handle resize-handle-sw"
                @pointerdown.stop="startResize($event, item, 'sw')"
              ></div>
              <div
                v-if="editMode && item.wareType !== 2"
                class="resize-handle resize-handle-se"
                @pointerdown.stop="startResize($event, item, 'se')"
              ></div>
              <canvas
                v-if="
                  !scalingInProgress &&
                  (highlightedLocations.size
                    ? highlightedLocations.has(item.name)
                    : showHeatmap)
                "
                class="pallet-canvas"
                :width="item.w"
                :height="item.h"
                :ref="(el) => setCanvasRef(el, item.id)"
              ></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="float-zoom">
        <button class="fb-btn" @click="zoomOut" title="缩小">−</button>
        <span class="fb-zoom">{{ Math.round(effectiveScale * 100) }}%</span>
        <button class="fb-btn" @click="zoomIn" title="放大">+</button>
        <div class="fb-divider"></div>
        <button
          class="fb-btn fb-btn-text"
          @click="fitToContent"
          title="自适应所有库位"
        >
          自适应
        </button>
        <button class="fb-btn" @click="resetView" title="重置">↻</button>
        <template v-if="highlightedLocations.size">
          <div class="fb-divider"></div>
          <button
            class="fb-btn fb-btn-text"
            @click="clearHighlights"
            title="清除高亮"
          >
            清除({{ highlightedLocations.size }})
          </button>
        </template>
      </div>

      <div v-if="editMode && selectedBatchItems.length" class="float-batch">
        <span class="batch-count"
          >已选{{ selectedBatchItems.length }}个库位</span
        >
        <div class="fb-divider"></div>
        <button
          class="fb-btn fb-btn-text"
          @click="batchAlignH"
          title="所有选中库位Y坐标对齐到最上方"
        >
          上对齐
        </button>
        <button
          class="fb-btn fb-btn-text"
          @click="batchAlignV"
          title="所有选中库位X坐标对齐到最左方"
        >
          左对齐
        </button>
        <button
          class="fb-btn fb-btn-text"
          @click="batchPackH"
          title="选中库位紧密竖排，边缘贴合"
        >
          竖排贴合
        </button>
        <button
          class="fb-btn fb-btn-text"
          @click="batchPackV"
          title="选中库位紧密横排，边缘贴合"
        >
          横排贴合
        </button>
        <button class="fb-btn fb-btn-text" @click="batchDistributeH">
          横均分
        </button>
        <button class="fb-btn fb-btn-text" @click="batchDistributeV">
          竖均分
        </button>
        <button
          class="fb-btn fb-btn-text"
          @click="batchSortByName"
          title="按编号排序后竖排排列"
        >
          编号排序
        </button>
        <button
          class="fb-btn fb-btn-text"
          @click="batchRotate"
          title="交换选中库位的宽高"
        >
          批量旋转
        </button>
        <button
          v-if="
            selectedBatchItems.length === 1 &&
            ![2].includes(selectedBatchItems[0].wareType)
          "
          class="fb-btn fb-btn-text"
          @click="openCopyWareDialog"
        >
          复制
        </button>
        <button
          v-if="
            selectedBatchItems.length === 1 &&
            selectedBatchItems[0].wareType === 2
          "
          class="fb-btn fb-btn-text"
          @click="openResizeWareDialog"
        >
          修改尺寸
        </button>
        <div class="fb-divider"></div>
        <button
          class="fb-btn fb-btn-text fb-btn-danger"
          @click="batchReturnToUnplaced"
          title="退回选中库位到未摆放区域"
        >
          退回
        </button>
      </div>
    </div>

    <div
      class="bottom-drawer"
      v-if="editMode"
      :class="{ collapsed: !drawerOpen }"
    >
      <div
        class="drawer-header"
        @click="
          drawerOpen = !drawerOpen;
          $nextTick(() => fitToContent());
        "
      >
        <span class="drawer-title"
          >未摆放库位 ({{ unplacedLocations.length }})</span
        >
        <div class="drawer-header-right">
          <button
            v-if="unplacedLocations.length > 1"
            class="sort-btn"
            @click.stop="toggleSortOrder"
            :title="sortOrder === 'asc' ? '正序 (A→Z)' : '倒序 (Z→A)'"
          >
            {{ sortOrder === "asc" ? "A↑" : "Z↓" }}
          </button>
          <button
            v-if="drawerFilteredLocations.length"
            class="batch-place-btn"
            @click.stop="batchPlaceAll"
          >
            全部摆放{{
              drawerFilteredLocations.length < unplacedLocations.length
                ? " (" + drawerFilteredLocations.length + ")"
                : ""
            }}
          </button>
          <span class="drawer-arrow">{{ drawerOpen ? "▼" : "▲" }}</span>
        </div>
      </div>
      <!-- 类型筛选标签 - 只显示数量大于0的 -->
      <div
        v-show="drawerOpen && unplacedLocations.length > 0"
        class="drawer-type-filter"
      >
        <div
          v-for="opt in drawerTypeOptions.filter(
            (o) => getUnplacedCountByType(o.value) > 0,
          )"
          :key="opt.value"
          class="type-tag"
          :class="{ active: drawerFilterType === opt.value }"
          :style="{
            borderColor: opt.color,
            color: drawerFilterType === opt.value ? '#fff' : opt.color,
            backgroundColor:
              drawerFilterType === opt.value ? opt.color : 'transparent',
          }"
          @click="
            drawerFilterType = drawerFilterType === opt.value ? null : opt.value
          "
        >
          {{ opt.label }}
          <span class="type-count"
            >({{ getUnplacedCountByType(opt.value) }})</span
          >
        </div>
      </div>
      <div class="drawer-body" v-show="drawerOpen">
        <div v-if="!editMode" class="drawer-hint">
          开启编辑模式后可拖拽库位到画布
        </div>
        <div v-if="unplacedLocations.length === 0" class="drawer-empty">
          暂无未摆放库位
        </div>
        <template v-else>
          <div class="drawer-search-row">
            <div class="drawer-search-wrap" v-if="unplacedLocations.length > 5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#64748b"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                v-model="drawerSearchKey"
                class="drawer-search-input"
                placeholder="搜索库位"
              />
              <span
                v-if="drawerSearchKey"
                class="drawer-search-clear"
                @click="drawerSearchKey = ''"
                >✕</span
              >
            </div>
            <div
              v-if="editMode"
              class="drawer-search-wrap drawer-highlight-wrap"
            >
              <span class="highlight-label">点亮</span>
              <input
                v-model="editSearchPrefix"
                class="drawer-search-input highlight-input"
                placeholder="首字母"
                @input="highlightByPrefix"
              />
              <span
                v-if="editSearchPrefix"
                class="drawer-search-clear"
                @click="clearEditSearch"
                >✕</span
              >
              <button
                v-if="highlightedLocations.size > 0"
                class="highlight-select-btn"
                @click="selectHighlighted"
              >
                选中({{ highlightedLocations.size }})
              </button>
            </div>
          </div>
          <div v-if="drawerFilteredLocations.length === 0" class="drawer-empty">
            无匹配库位
          </div>
          <div class="drawer-scroll" v-else>
            <div
              v-for="u in drawerFilteredLocations"
              :key="u.id"
              class="drawer-item"
              :draggable="editMode"
              :class="{ disabled: !editMode }"
              @dragstart="handleUnplacedDragStart($event, u)"
              @dragend="handleUnplacedDragEnd"
              @dblclick="handleUnplacedDblClick(u)"
            >
              <div class="drawer-item-name">{{ u.name }}</div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="showSearchPanel"
      class="search-panel-overlay"
      @click="closeSearchPanel"
    >
      <div class="search-panel" @click.stop>
        <!-- 供应商搜索区 -->
        <div class="sp-supplier-section">
          <div class="sp-section-header">
            <span class="sp-section-label">供应商</span>
            <button
              v-if="searchSelectedSupplier"
              class="sp-clear-btn"
              @click="clearSupplierFilter"
            >
              清除筛选
            </button>
          </div>
          <div class="sp-search-inline">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#64748b"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              v-model="searchKeyword"
              class="sp-input-inline"
              placeholder="输入供应商名称"
              @input="searchSupplier"
            />
          </div>
          <div class="sp-supplier-list">
            <div
              v-if="searchLoading && !searchMaterialKeyword"
              class="sp-hint-sm"
            >
              搜索中...
            </div>
            <div v-else-if="searchSuppliers.length" class="sp-items">
              <div
                v-for="(s, i) in searchSuppliers"
                :key="'s' + i"
                class="sp-item"
                :class="{
                  'sp-item-active': searchSelectedSupplier === s.supplierName,
                }"
                @click="selectSupplier(s)"
              >
                <span class="sp-item-name">{{ s.supplierName }}</span>
                <span class="sp-item-arrow">›</span>
              </div>
            </div>
            <div
              v-else-if="searchKeyword && searchKeyword.length >= 2"
              class="sp-hint-sm"
            >
              未找到
            </div>
            <div v-else class="sp-hint-sm">输入供应商名称搜索</div>
          </div>
        </div>

        <!-- 物料搜索区 -->
        <div class="sp-material-section">
          <div class="sp-section-header">
            <span class="sp-section-label">物料编码</span>
            <span v-if="searchMaterialResults.length" class="sp-section-count"
              >{{ searchMaterialResults.length }}条</span
            >
          </div>
          <div class="sp-search-inline">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#64748b"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              v-model="searchMaterialKeyword"
              class="sp-input-inline"
              :placeholder="
                searchSelectedSupplier
                  ? '在' + searchSelectedSupplier + '下搜索物料编码'
                  : '输入物料编码搜索'
              "
              @input="searchMaterial"
            />
          </div>
          <div class="sp-material-toolbar" v-if="searchMaterialResults.length">
            <button
              class="sp-tool-btn"
              @click="highlightAllMaterials"
              :disabled="!!highlightProgress"
            >
              {{ highlightProgress || "逐个点亮全部" }}
            </button>
            <button
              v-if="highlightRunning"
              class="sp-tool-btn sp-cancel-btn"
              @click="cancelHighlight"
            >
              取消
            </button>
          </div>
          <div class="sp-material-list">
            <div
              v-if="searchLoading && searchMaterialKeyword"
              class="sp-hint-sm"
            >
              搜索中...
            </div>
            <div v-else-if="searchMaterialResults.length" class="sp-items">
              <div
                v-for="(m, i) in searchMaterialResults"
                :key="'m' + i"
                class="sp-item sp-material-item"
                @click="selectMaterial(m)"
              >
                <div class="sp-mat-info">
                  <div class="sp-mat-code">{{ m.materialNo }}</div>
                  <div class="sp-mat-sup" v-if="m.supplierName">
                    {{ m.supplierName }}
                  </div>
                  <div class="sp-mat-desc" v-if="m.materialDesc">
                    {{ m.materialDesc }}
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="
                searchMaterialKeyword &&
                searchMaterialKeyword.length >= 2 &&
                !searchLoading
              "
              class="sp-hint-sm"
            >
              未找到匹配物料
            </div>
            <div v-else-if="!searchMaterialKeyword" class="sp-hint-sm">
              {{
                searchSelectedSupplier
                  ? "搜索该供应商下的物料编码"
                  : "输入物料编码搜索"
              }}
            </div>
          </div>
        </div>

        <!-- 底部关闭按钮 -->
        <div class="sp-footer">
          <button class="sp-close-btn" @click="closeSearchPanel">
            {{ highlightRunning ? "隐藏" : "关闭" }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDialog" class="dialog-overlay" @click="closeDialog">
      <div class="dialog-box" @click.stop>
        <div class="dialog-header">
          <div class="dialog-title-wrap">
            <h3>库位详情</h3>
            <div class="dialog-location-name">
              {{
                currentLocationDetail?.locationName || currentItem?.name || "-"
              }}
            </div>
          </div>
          <button class="dialog-close-btn" @click="closeDialog">✕</button>
        </div>
        <div class="dialog-body">
          <div v-if="detailLoading" class="detail-loading">
            <div class="loading-spinner"></div>
            <span>加载中...</span>
          </div>
          <div v-else-if="detailError" class="detail-error">
            {{ detailError }}
          </div>
          <div v-else-if="!displayHasLocationMaterials" class="detail-empty">
            当前库位为空
          </div>
          <div v-else class="supplier-list">
            <div
              class="supplier-block"
              v-for="(g, gi) in getSupplierGroups(displayLocationDetail)"
              :key="g.supplierName + '-' + gi"
            >
              <div
                class="supplier-header"
                @click="toggleSupplierCollapse(g.supplierName)"
              >
                <div class="supplier-title">{{ g.supplierName }}</div>
                <div class="supplier-meta">
                  <span class="supplier-count">{{ g.items.length }}种物料</span>
                  <span class="supplier-arrow">{{
                    isSupplierCollapsed(g.supplierName) ? "▶" : "▼"
                  }}</span>
                </div>
              </div>
              <div
                class="material-list"
                v-show="!isSupplierCollapsed(g.supplierName)"
              >
                <div
                  v-for="(m, i) in g.items"
                  :key="m.materialNo + '-' + i"
                  class="material-item"
                  :class="{
                    active:
                      selectedMaterialNo && m.materialNo === selectedMaterialNo,
                  }"
                >
                  <div class="material-info">
                    <div class="material-no">{{ m.materialNo || "-" }}</div>
                    <div class="material-desc">{{ m.materialDesc || "-" }}</div>
                  </div>
                  <div class="material-stock">
                    <div class="stock-item stock-good">
                      <span class="stock-num">{{
                        formatStockNum(m.goodStockNum)
                      }}</span>
                      <span class="stock-label">良品</span>
                    </div>
                    <div class="stock-item stock-bad">
                      <span class="stock-num">{{
                        formatStockNum(m.badStockNum)
                      }}</span>
                      <span class="stock-label">不良品</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-footer-btn" @click="closeDialog">关闭</button>
        </div>
      </div>
    </div>

    <div
      v-if="isMobile && mobileSearchOpen"
      class="mobile-drawer-overlay"
      @click="toggleMobileSearch"
    >
      <div class="mobile-drawer" @click.stop>
        <div class="mobile-drawer-header">
          <button
            v-if="searchState !== 'idle'"
            class="mobile-drawer-close"
            @click="searchBack"
          >
            ← 返回
          </button>
          <div class="mobile-drawer-title">
            {{
              searchState === "idle"
                ? "搜索"
                : searchState === "supplier"
                ? "选择供应商"
                : searchState === "material-code"
                ? "物料编码搜索"
                : "物料列表"
            }}
          </div>
          <button class="mobile-drawer-close" @click="toggleMobileSearch">
            关闭
          </button>
        </div>
        <div v-if="searchState === 'idle'" class="mobile-search-tabs">
          <button
            class="mobile-tab-btn"
            :class="{ active: mobileSearchTab === 'supplier' }"
            @click="mobileSearchTab = 'supplier'"
          >
            按供应商搜索
          </button>
          <button
            class="mobile-tab-btn"
            :class="{ active: mobileSearchTab === 'material' }"
            @click="mobileSearchTab = 'material'"
          >
            按物料编码搜索
          </button>
        </div>
        <div
          v-if="searchState === 'idle' && mobileSearchTab === 'supplier'"
          class="search-section"
        >
          <input
            v-model="searchKeyword"
            class="search-input"
            placeholder="输入供应商名称"
            @input="searchSupplier"
          />
        </div>
        <div
          v-if="searchState === 'idle' && mobileSearchTab === 'supplier'"
          class="search-section"
        >
          <div v-if="searchLoading" class="search-hint">搜索中...</div>
          <div
            v-else-if="
              searchSuppliers.length === 0 &&
              searchKeyword &&
              searchKeyword.length >= 2
            "
            class="search-hint"
          >
            未找到匹配供应商
          </div>
          <div v-else-if="searchSuppliers.length === 0" class="search-hint">
            输入供应商名称搜索
          </div>
          <div v-else class="result-list">
            <div
              v-for="(s, i) in searchSuppliers"
              :key="i"
              class="result-item"
              @click="selectSupplier(s)"
            >
              <div class="result-main">
                <div class="result-title">{{ s.supplierName }}</div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="searchState === 'idle' && mobileSearchTab === 'material'"
          class="search-section"
        >
          <input
            v-model="searchMaterialKeyword"
            class="search-input"
            placeholder="输入物料编码"
            @input="searchMaterial"
          />
        </div>
        <div
          v-if="searchState === 'idle' && mobileSearchTab === 'material'"
          class="search-section"
        >
          <div v-if="searchLoading" class="search-hint">搜索中...</div>
          <div
            v-else-if="
              searchMaterialResults.length === 0 &&
              searchMaterialKeyword &&
              searchMaterialKeyword.length >= 2
            "
            class="search-hint"
          >
            未找到匹配物料
          </div>
          <div
            v-else-if="searchMaterialResults.length === 0"
            class="search-hint"
          >
            输入物料编码搜索
          </div>
          <div v-else class="result-list">
            <div
              class="mobile-highlight-all"
              v-if="searchMaterialResults.length"
            >
              <button
                class="mobile-highlight-btn"
                @click="mobileHighlightAll"
                :disabled="!!highlightProgress"
              >
                {{
                  highlightProgress ||
                  "点亮全部 (" + searchMaterialResults.length + ")"
                }}
              </button>
              <button
                v-if="highlightRunning"
                class="mobile-cancel-btn"
                @click="cancelHighlight"
              >
                取消
              </button>
            </div>
            <div
              v-for="(m, i) in searchMaterialResults"
              :key="i"
              class="result-item"
              @click="mobileHighlightSingle(m)"
            >
              <div class="result-main">
                <div class="result-title">{{ m.materialNo }}</div>
                <div class="result-sub">{{ m.materialDesc || "-" }}</div>
                <div class="result-supplier" v-if="m.supplierName">
                  供应商: {{ m.supplierName }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="searchState === 'supplier' || searchState === 'material'"
          class="search-section"
        >
          <div class="mobile-supplier-header">
            <span class="mobile-supplier-label">供应商:</span>
            <span class="mobile-supplier-name">{{
              searchSelectedSupplier
            }}</span>
          </div>
          <div class="search-section" style="padding: 0 0 8px 0">
            <input
              v-model="searchMaterialKeyword"
              class="search-input"
              placeholder="搜索该供应商下的物料"
              @input="searchMaterialInSupplier"
            />
          </div>
          <div v-if="searchLoading" class="search-hint">加载中...</div>
          <div
            v-else-if="filteredSupplierMaterials.length === 0"
            class="search-hint"
          >
            该供应商下暂无物料
          </div>
          <div v-else class="result-list">
            <div
              class="mobile-highlight-all"
              v-if="filteredSupplierMaterials.length"
            >
              <button
                class="mobile-highlight-btn"
                @click="mobileHighlightAllSupplier"
                :disabled="!!highlightProgress"
              >
                {{
                  highlightProgress ||
                  "点亮全部 (" + filteredSupplierMaterials.length + ")"
                }}
              </button>
              <button
                v-if="highlightRunning"
                class="mobile-cancel-btn"
                @click="cancelHighlight"
              >
                取消
              </button>
            </div>
            <div
              v-for="(m, i) in filteredSupplierMaterials"
              :key="i"
              class="result-item mobile-material-item"
              @click="selectMaterial(m)"
            >
              <div class="result-main">
                <div class="result-title">{{ m.materialNo }}</div>
                <div class="result-sub">{{ m.materialDesc || "-" }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 复制库位/墙体弹窗 -->
    <div
      v-if="copyWareDialogVisible"
      class="dialog-overlay"
      @click="copyWareDialogVisible = false"
    >
      <div class="modal-box copy-ware-modal" @click.stop>
        <div class="modal-box-title">复制{{ copyWareTypeLabel }}</div>
        <div class="modal-box-body">
          <div class="modal-field">
            <label class="modal-field-label">源{{ copyWareTypeLabel }}</label>
            <div class="modal-field-value copy-source">
              {{ copySourceName }}
            </div>
          </div>
          <!-- 名称模式：办公区域/齐套区/柱体等可自定义命名 -->
          <template v-if="copyWareMode === 'name'">
            <div class="modal-field">
              <label class="modal-field-label"
                >新{{ copyWareTypeLabel }}名称</label
              >
              <input
                v-model="copyWareNewName"
                type="text"
                class="modal-field-input"
                placeholder="请输入新名称"
                @keyup.enter="confirmCopyWare"
              />
            </div>
            <div class="modal-field-hint">
              将复制为：{{ copyWareNewName || "（未填写）" }}
            </div>
          </template>
          <!-- 数量模式：墙体/通道等批量复制 -->
          <template v-else>
            <div class="modal-field">
              <label class="modal-field-label">复制数量</label>
              <input
                v-model.number="copyWareCount"
                type="number"
                min="1"
                max="50"
                class="modal-field-input"
                @keyup.enter="confirmCopyWare"
              />
            </div>
            <div class="modal-field-hint">
              将生成：{{ copySourceName }}_1、{{ copySourceName }}_2 …（共
              {{ copyWareCount }} 个）
            </div>
          </template>
        </div>
        <div class="modal-box-btns">
          <button
            class="modal-btn modal-btn-cancel"
            @click="copyWareDialogVisible = false"
          >
            取消
          </button>
          <button
            class="modal-btn modal-btn-ok"
            :disabled="
              copyWareLoading ||
              (copyWareMode === 'name' && !copyWareNewName.trim())
            "
            @click="confirmCopyWare"
          >
            {{
              copyWareLoading
                ? copyWareMode === "name"
                  ? "复制中..."
                  : "复制中 " + copyWareProgress + "/" + copyWareCount
                : "确定"
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- 修改库位尺寸弹窗 -->
    <div
      v-if="resizeWareDialogVisible"
      class="dialog-overlay"
      @click="resizeWareDialogVisible = false"
    >
      <div class="modal-box" @click.stop>
        <div class="modal-box-title">修改库位尺寸</div>
        <div class="modal-box-body">
          <div class="modal-field">
            <label class="modal-field-label">库位名称</label>
            <div class="modal-field-value">{{ resizeWareName }}</div>
          </div>
          <div class="modal-field modal-field-row">
            <div class="modal-field-half">
              <label class="modal-field-label">横向格数</label>
              <input
                v-model.number="resizeWareWeight"
                type="number"
                min="1"
                max="20"
                class="modal-field-input"
                @keyup.enter="confirmResizeWare"
              />
            </div>
            <div class="modal-field-half">
              <label class="modal-field-label">纵向格数</label>
              <input
                v-model.number="resizeWareHeight"
                type="number"
                min="1"
                max="20"
                class="modal-field-input"
                @keyup.enter="confirmResizeWare"
              />
            </div>
          </div>
        </div>
        <div class="modal-box-btns">
          <button
            class="modal-btn modal-btn-cancel"
            @click="resizeWareDialogVisible = false"
          >
            取消
          </button>
          <button
            class="modal-btn modal-btn-ok"
            :disabled="resizeWareLoading"
            @click="confirmResizeWare"
          >
            {{ resizeWareLoading ? "保存中..." : "确定" }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="modalVisible" class="dialog-overlay" @click="modalCancel">
      <div class="modal-box" @click.stop>
        <div class="modal-box-title">{{ modalTitle }}</div>
        <div class="modal-box-body">{{ modalMessage }}</div>
        <div class="modal-box-btns">
          <button
            v-if="modalType === 'confirm'"
            class="modal-btn modal-btn-cancel"
            @click="modalCancel"
          >
            取消
          </button>
          <button class="modal-btn modal-btn-ok" @click="modalOk">确定</button>
        </div>
      </div>
    </div>

    <div
      v-if="showAreaPanel"
      class="area-panel-overlay"
      @click="showAreaPanel = false"
    >
      <div class="area-panel" @click.stop>
        <div class="area-panel-header">
          <span class="area-panel-title">仓库面积占用</span>
          <div class="area-header-right">
            <button
              class="area-refresh-btn"
              @click="openAreaPanel(true)"
              title="刷新数据"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M1 4v6h6" />
                <path d="M23 20v-6h-6" />
                <path
                  d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"
                />
              </svg>
            </button>
            <button class="area-panel-close" @click="showAreaPanel = false">
              ✕
            </button>
          </div>
        </div>
        <div v-if="areaLoading" class="area-loading">加载中...</div>
        <template v-else>
          <div class="area-panel-top">
            <div class="area-ring-wrap">
              <svg viewBox="0 0 120 120" class="area-ring-svg">
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="rgba(255,255,255,0.06)"
                  stroke-width="12"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  :stroke="getOccupyColor(areaTotal.occupyRate)"
                  stroke-width="12"
                  stroke-linecap="round"
                  :stroke-dasharray="getRingDash(areaTotal.occupyRate)"
                  stroke-dashoffset="0"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div class="area-ring-center">
                <span
                  class="area-ring-rate"
                  :class="getOccupyClass(areaTotal.occupyRate)"
                >
                  {{
                    areaTotal.occupyRate != null
                      ? areaTotal.occupyRate + "%"
                      : "--"
                  }}
                </span>
                <span class="area-ring-label">占用率</span>
              </div>
            </div>
            <div class="area-summary-col">
              <div class="area-stat-row">
                <span class="area-stat-label">租赁面积</span>
                <span class="area-stat-value">{{
                  areaTotal.rentArea != null
                    ? Number(areaTotal.rentArea).toFixed(2) + " m²"
                    : "--"
                }}</span>
              </div>
              <div class="area-stat-row">
                <span class="area-stat-label">占用面积</span>
                <span class="area-stat-value">{{
                  areaTotal.occupyArea != null
                    ? Number(areaTotal.occupyArea).toFixed(2) + " m²"
                    : "--"
                }}</span>
              </div>
            </div>
          </div>
          <div class="area-filter-row">
            <button
              v-for="f in areaFilters"
              :key="f.key"
              class="area-filter-btn"
              :class="{ active: areaFilterKey === f.key }"
              @click="areaFilterKey = areaFilterKey === f.key ? '' : f.key"
            >
              {{ f.label }}
              <span v-if="f.count > 0" class="area-filter-count">{{
                f.count
              }}</span>
            </button>
          </div>
          <div class="area-search-wrap">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#000000"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              v-model="areaSearchKey"
              class="area-search-input"
              placeholder="搜索供应商..."
            />
            <button
              v-if="areaSearchKey"
              class="area-search-clear"
              @click="areaSearchKey = ''"
            >
              ✕
            </button>
          </div>
          <div class="area-supplier-list">
            <div
              v-for="s in filteredAreaSuppliers"
              :key="s.supplierId"
              class="area-supplier-row"
              :class="getOccupyClass(s.occupyRate)"
              @click="openSupplierTrend(s)"
            >
              <div class="area-row-inner">
                <div class="area-row-name" :title="s.supplierName">
                  {{ s.supplierName }}
                </div>
                <div class="area-row-pct">
                  {{ s.occupyRate != null ? s.occupyRate : "--"
                  }}<span class="area-pct-unit">%</span>
                </div>
              </div>
              <div class="area-row-meta">
                <span class="area-meta-rent"
                  >租赁 {{ formatAreaShort(s.rentArea) }}</span
                >
                <span class="area-meta-occupy"
                  >占用 {{ formatAreaShort(s.occupyArea) }}</span
                >
                <span class="area-meta-bar">
                  <span class="area-bar-track">
                    <span
                      class="area-bar-fill"
                      :style="{ width: Math.min(100, s.occupyRate || 0) + '%' }"
                    ></span>
                  </span>
                </span>
              </div>
            </div>
            <div v-if="!filteredAreaSuppliers.length" class="area-empty">
              暂无供应商数据
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 供应商历史占用趋势弹窗 -->
    <div
      v-if="supplierTrendVisible"
      class="dialog-overlay"
      @click="closeSupplierTrend"
    >
      <div class="modal-box supplier-trend-modal" @click.stop>
        <div class="modal-box-title">
          {{ trendSupplier?.supplierName || "供应商占用趋势" }}
        </div>
        <div class="trend-toolbar">
          <label class="trend-date-label">开始</label>
          <input
            v-model="trendStartDate"
            type="date"
            class="trend-date-input"
          />
          <label class="trend-date-label">结束</label>
          <input v-model="trendEndDate" type="date" class="trend-date-input" />
          <button
            class="modal-btn modal-btn-ok trend-query-btn"
            :disabled="trendLoading"
            @click="loadSupplierTrend"
          >
            {{ trendLoading ? "查询中..." : "查询" }}
          </button>
        </div>
        <div class="trend-chart-wrap">
          <div v-if="trendLoading" class="trend-loading">加载中...</div>
          <div v-else-if="trendError" class="trend-error">{{ trendError }}</div>
          <div v-else-if="!trendPoints.length" class="trend-empty">
            该时间范围内暂无占用数据
          </div>
          <div v-else class="trend-charts-stack">
            <!-- 上图：面积（租用+已用） -->
            <div class="trend-chart-block">
              <div class="trend-chart-title">面积趋势（m²）</div>
              <div ref="trendChartArea" class="trend-chart-inner"></div>
            </div>
            <!-- 下图：占用率 -->
            <div class="trend-chart-block">
              <div class="trend-chart-title">占用率趋势（%）</div>
              <div ref="trendChartRate" class="trend-chart-inner"></div>
            </div>
          </div>
        </div>
        <div v-if="trendStats" class="trend-stats">
          <div class="trend-stats-row">
            <span class="trend-stats-tag tag-rate">占用率</span>
            <span
              >峰值 <b class="num-rate">{{ trendStats.maxRate }}%</b></span
            >
            <span
              >均值 <b class="num-rate">{{ trendStats.avgRate }}%</b></span
            >
          </div>
          <div class="trend-stats-row">
            <span class="trend-stats-tag tag-rent">租用面积</span>
            <span
              >峰值 <b class="num-rent">{{ trendStats.maxRent }}</b> m²</span
            >
            <span
              >均值 <b class="num-rent">{{ trendStats.avgRent }}</b> m²</span
            >
          </div>
          <div class="trend-stats-row">
            <span class="trend-stats-tag tag-occupy">已用面积</span>
            <span
              >峰值
              <b class="num-occupy">{{ trendStats.maxOccupy }}</b> m²</span
            >
            <span
              >均值
              <b class="num-occupy">{{ trendStats.avgOccupy }}</b> m²</span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- ── 非编辑模式底部概况栏（仅 PC 端） ── -->
    <div
      v-if="
        !editMode &&
        selectedWareId &&
        String(selectedWareId).length > 0 &&
        !isMobile
      "
      class="bottom-summary-bar"
    >
      <div class="summary-project">{{ currentProjectName }}</div>
      <div class="summary-divider"></div>
      <div class="summary-item">
        <span class="summary-label">供应商</span>
        <span class="summary-value">{{ currentSupplierCount }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-item">
        <span class="summary-label">库位</span>
        <span class="summary-value">{{ placedLocationCount }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-item">
        <span class="summary-label">租赁面积</span>
        <span class="summary-value">{{ summaryRentArea }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-item">
        <span class="summary-label">占用面积</span>
        <span class="summary-value">{{ summaryOccupyArea }}</span>
      </div>
      <div class="summary-divider"></div>
      <div
        class="summary-item summary-rate"
        :class="summaryRateLevel"
        :title="'总占用率'"
      >
        <span class="summary-label">占用率</span>
        <span class="summary-value">{{ summaryOccupyRate }}</span>
      </div>
      <div class="summary-divider"></div>
      <!-- 热力图图例（可拖动对比） -->
      <div
        class="summary-legend"
        ref="legendRef"
        :class="{ 'is-dragging': legendDragging, 'is-back': legendReturning }"
        :style="legendStyle"
        @pointerdown="onLegendDragStart"
        @pointermove="onLegendDragMove"
        @pointerup="onLegendDragEnd"
        @pointercancel="onLegendDragEnd"
      >
        <span class="legend-title">占用率</span>
        <span
          v-for="step in heatmapLegend"
          :key="step.pct"
          class="legend-swatch-cell"
          :title="step.pct"
        >
          <span
            class="legend-swatch"
            :style="{
              background: 'rgb(' + step.r + ',' + step.g + ',' + step.b + ')',
            }"
          ></span>
          <span class="legend-swatch-label">{{ step.pct }}</span>
        </span>
        <span class="legend-text">低→高</span>
      </div>
    </div>

    <div v-if="toastVisible" class="toast-msg" :class="'toast-' + toastType">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import { getUicAuthUserInfo } from "@/utils/auth";
import * as echarts from "echarts";

export default {
  name: "WarehouseMain",
  data() {
    return {
      inventoryData: [],
      unplacedLocations: [],
      renderedCount: 0,
      renderChunkSize: 50,
      renderTimer: null,
      layoutRawPlacedCount: 0,
      layoutRawUnplacedCount: 0,
      searchKey: "",
      searchResults: [],
      searchLoading: false,
      selectedLocationKey: "",
      selectedMaterialNo: "",
      draggingUnplacedId: "",
      draggedUnplacedItem: null,

      bizDept: "",
      wareLoading: false,
      wareList: [],
      selectedWareId: "",
      layoutLoading: false,

      stockId: "",
      detailLoading: false,
      detailError: "",
      currentLocationDetail: null,

      selectedSupplierName: "",

      editMode: false,
      showDialog: false,
      copyWareDialogVisible: false,
      copySourceName: "",
      copyWareTypeLabel: "元素",
      copyWareCount: 1,
      copyWareProgress: 0,
      copyWareLoading: false,
      copyWareMode: "count", // 'count'=数量模式(墙/通道) | 'name'=名称模式(办公区域/齐套区等)
      copyWareNewName: "",
      resizeWareDialogVisible: false,
      resizeWareId: null,
      resizeWareName: "",
      resizeWareWeight: 1,
      resizeWareHeight: 1,
      resizeWareLoading: false,
      currentItem: null,
      warehouse: { minX: 0, maxX: 99999, minY: 0, maxY: 99999 },
      originalData: [],
      userScale: 1,
      fitScale: 1,
      minUserScale: 0.25,
      maxUserScale: 10,
      mobileSearchTab: "supplier",
      baseCanvasWidth: 1200,
      baseCanvasHeight: 680,
      canvasMinWidth: 1200,
      canvasMinHeight: 680,
      resizeObserver: null,
      resizing: false,
      lastTouchEndTs: 0,
      lastTouchEndId: "",

      gridSize: 20,
      baseCellW: 40,
      baseCellH: 40,
      showHeatmap: true,
      scalingInProgress: false,
      showAreaPanel: false,
      areaLoading: false,
      areaSearchKey: "",
      areaFilterKey: "",
      areaSuppliers: [],
      areaTotal: { rentArea: null, occupyArea: null, occupyRate: null },
      supplierTrendVisible: false,
      trendSupplier: null,
      trendLoading: false,
      trendError: "",
      trendStartDate: "",
      trendEndDate: "",
      trendPoints: [],
      trendStats: null,
      trendChartArea: null,
      trendChartRate: null,
      palletStyleCache: {},
      snapToGrid: true,

      mobileSearchOpen: false,
      viewportPointers: new Map(),
      pinchStartDist: 0,
      pinchStartScale: 1,
      pinchStartEffectiveScale: 1,
      pinchStartScrollLeft: 0,
      pinchStartScrollTop: 0,
      pinchStartCenterX: 0,
      pinchStartCenterY: 0,
      pinchPadLeft: 0,
      pinchPadTop: 0,
      pinchRaf: 0,
      pinchPendingScale: null,

      panPointerId: null,
      panStartClientX: 0,
      panStartClientY: 0,
      panStartScrollLeft: 0,
      panStartScrollTop: 0,
      panRaf: 0,
      panPending: null,

      guideLines: { v: [], h: [] },

      supplierCollapsed: {},
      selectedBatchItems: [],
      modalVisible: false,
      modalType: "alert",
      modalTitle: "",
      modalMessage: "",
      modalResolve: null,
      toastVisible: false,
      toastMessage: "",
      toastType: "success",
      resizeDebounceFlag: false,
      canvasSizeTimer: null,
      drawerOpen: true,
      drawerSearchKey: "",
      sortOrder: "asc",
      batchSortDesc: false,
      fitDone: false,
      showSearchPanel: false,
      // 热力图图例拖动
      legendDragging: false,
      legendReturning: false,
      legendOffsetX: 0,
      legendOffsetY: 0,
      legendStartX: 0,
      legendStartY: 0,
      legendPointerId: null,
      legendBaseLeft: 0,
      legendBaseBottom: 0,
      searchState: "idle",
      searchKeyword: "",
      searchMaterialKeyword: "",
      searchSuppliers: [],
      searchMaterials: [],
      searchMaterialResults: [],
      searchSelectedSupplier: "",
      highlightProgress: "",
      highlightRunning: false,
      highlightCancelled: false,
      highlightedLocations: new Set(),
      editSearchPrefix: "",
      drawerFilterType: null,
      drawerTypeOptions: [
        { value: 0, label: "仓库", color: "#3b82f6" },
        { value: 1, label: "库区", color: "#8b5cf6" },
        { value: 2, label: "库位", color: "#10b981" },
        { value: 3, label: "虚拟仓库", color: "#6b7280" },
        { value: 4, label: "墙体", color: "#374151" },
        { value: 5, label: "通道", color: "#f59e0b" },
        { value: 6, label: "大门", color: "#ef4444" },
        { value: 7, label: "柱体", color: "#6366f1" },
        { value: 8, label: "电梯", color: "#06b6d4" },
        { value: 9, label: "楼梯", color: "#84cc16" },
        { value: 10, label: "办公区域", color: "#ec4899" },
        { value: 11, label: "齐套区", color: "#14b8a6" },
      ],
    };
  },
  computed: {
    visibleInventoryData() {
      return this.inventoryData.slice(0, this.renderedCount);
    },
    filteredSupplierMaterials() {
      if (!this.searchMaterialKeyword) return this.searchMaterials;
      const kw = this.searchMaterialKeyword.toLowerCase();
      return this.searchMaterials.filter(
        (m) =>
          (m.materialNo || "").toLowerCase().includes(kw) ||
          (m.materialDesc || "").toLowerCase().includes(kw),
      );
    },
    effectiveScale() {
      return this.fitScale * this.userScale;
    },
    areaFilters() {
      const list = this.areaSuppliers;
      return [
        {
          key: "free",
          label: "空闲",
          rule: "<30%",
          count: list.filter((s) => s.occupyRate != null && s.occupyRate < 30)
            .length,
        },
        {
          key: "normal",
          label: "正常",
          rule: "30%~100%",
          count: list.filter(
            (s) =>
              s.occupyRate != null && s.occupyRate >= 30 && s.occupyRate <= 100,
          ).length,
        },
        {
          key: "over",
          label: "超面积",
          rule: "100%~110%",
          count: list.filter(
            (s) =>
              s.occupyRate != null && s.occupyRate > 100 && s.occupyRate <= 110,
          ).length,
        },
        {
          key: "full",
          label: "严重超面积",
          rule: ">110%",
          count: list.filter((s) => s.occupyRate != null && s.occupyRate > 110)
            .length,
        },
      ];
    },
    filteredAreaSuppliers() {
      let list = this.areaSuppliers;
      if (this.areaFilterKey) {
        list = list.filter((s) => {
          const r = s.occupyRate;
          if (r == null) return false;
          switch (this.areaFilterKey) {
            case "free":
              return r < 30;
            case "normal":
              return r >= 30 && r <= 100;
            case "over":
              return r > 100 && r <= 110;
            case "full":
              return r > 110;
            default:
              return true;
          }
        });
      }
      if (this.areaSearchKey) {
        const kw = this.areaSearchKey.toLowerCase();
        list = list.filter((s) =>
          (s.supplierName || "").toLowerCase().includes(kw),
        );
      }
      return list;
    },
    drawerFilteredLocations() {
      let list = this.unplacedLocations;
      // 按类型筛选
      if (this.drawerFilterType !== null) {
        list = list.filter(
          (u) => (u.wareType ?? u.ware_type ?? 2) === this.drawerFilterType,
        );
      }
      if (this.drawerSearchKey) {
        const kw = this.drawerSearchKey.toLowerCase();
        list = list.filter((u) => (u.name || "").toLowerCase().startsWith(kw));
      }
      const sorted = [...list].sort((a, b) => {
        const na = (a.name || a.locationNo || "").toString();
        const nb = (b.name || b.locationNo || "").toString();
        return na.localeCompare(nb, "zh-CN", { numeric: true });
      });
      return this.sortOrder === "asc" ? sorted : sorted.reverse();
    },
    stageWidth() {
      const s = Number(this.effectiveScale) || 1;
      return Math.max(0, (Number(this.baseCanvasWidth) || 0) * s);
    },
    stageHeight() {
      const s = Number(this.effectiveScale) || 1;
      return Math.max(0, (Number(this.baseCanvasHeight) || 0) * s);
    },
    hasLocationMaterials() {
      const mats = this.currentLocationDetail?.materials;
      return Array.isArray(mats) && mats.length > 0;
    },
    displayLocationDetail() {
      const detail = this.currentLocationDetail;
      if (!detail) return null;
      const mats = Array.isArray(detail?.materials) ? detail.materials : [];
      const hasFilter = !!(
        this.selectedMaterialNo || this.selectedSupplierName
      );
      if (!hasFilter) return detail;
      const filtered = mats.filter((m) => {
        const okMaterial = this.selectedMaterialNo
          ? m?.materialNo === this.selectedMaterialNo
          : true;
        const okSupplier = this.selectedSupplierName
          ? m?.supplierName === this.selectedSupplierName
          : true;
        return okMaterial && okSupplier;
      });
      return {
        ...detail,
        materials: filtered,
      };
    },
    displayHasLocationMaterials() {
      const mats = this.displayLocationDetail?.materials;
      return Array.isArray(mats) && mats.length > 0;
    },
    isMobile() {
      try {
        if (typeof window === "undefined") return false;
        if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
          return true;
        const w = window.innerWidth || 0;
        return w > 0 && w <= 768;
      } catch (_) {
        return false;
      }
    },
    // ── 非编辑模式概况栏 ──
    currentProjectName() {
      // 优先从 supplier 接口返回的 deptName 取（接口里有项目组名称）
      if (this.areaSuppliers && this.areaSuppliers.length) {
        const deptSet = new Set();
        for (const s of this.areaSuppliers) {
          if (s.deptName) deptSet.add(s.deptName);
        }
        if (deptSet.size === 1) return [...deptSet][0];
        if (deptSet.size > 1) return [...deptSet].join(" / ");
      }
      // 兑底从 wareList 取
      const w = this.wareList.find((x) => x.id === this.selectedWareId);
      if (w) return w.wareName || w.name || w.locationNo || "—";
      return this.selectedWareId || "—";
    },
    placedLocationCount() {
      const list = this.inventoryData || this.placedLocations || [];
      return list.filter((item) => Number(item.wareType) === 2).length;
    },
    currentSupplierCount() {
      return (this.areaSuppliers || []).filter((s) => s.rentArea > 0).length;
    },
    summaryRentArea() {
      const v = this.areaTotal && this.areaTotal.rentArea;
      if (v == null || isNaN(v)) return "—";
      return Number(v).toFixed(2) + " m²";
    },
    summaryOccupyArea() {
      const v = this.areaTotal && this.areaTotal.occupyArea;
      if (v == null || isNaN(v)) return "—";
      return Number(v).toFixed(2) + " m²";
    },
    summaryOccupyRate() {
      const v = this.areaTotal && this.areaTotal.occupyRate;
      if (v == null || isNaN(v)) return "—";
      return Math.round(v) + "%";
    },
    summaryRateLevel() {
      const v = this.areaTotal && this.areaTotal.occupyRate;
      if (v == null) return "rate-empty";
      if (v >= 90) return "rate-danger";
      if (v >= 70) return "rate-warn";
      return "rate-normal";
    },
    heatmapLegend() {
      return [
        { pct: "0%", r: 0, g: 176, b: 80 },
        { pct: "10%", r: 46, g: 178, b: 66 },
        { pct: "20%", r: 92, g: 181, b: 51 },
        { pct: "30%", r: 139, g: 184, b: 37 },
        { pct: "40%", r: 185, g: 187, b: 22 },
        { pct: "50%", r: 231, g: 190, b: 8 },
        { pct: "60%", r: 255, g: 175, b: 0 },
        { pct: "70%", r: 255, g: 140, b: 0 },
        { pct: "80%", r: 255, g: 105, b: 0 },
        { pct: "90%", r: 255, g: 70, b: 0 },
        { pct: "100%", r: 255, g: 35, b: 0 },
        { pct: "100%+", r: 255, g: 0, b: 0 },
      ];
    },
    legendStyle() {
      // 拖动中：实时跟随手指/鼠标
      if (this.legendDragging) {
        return {
          position: "fixed",
          left: this.legendBaseLeft + this.legendOffsetX + "px",
          top: this.legendBaseBottom - 44 + this.legendOffsetY + "px",
          bottom: "auto",
          right: "auto",
          zIndex: 9999,
          transition: "none",
          cursor: "grabbing",
        };
      }
      // 返航中：使用 CSS transition 平滑回原位
      if (this.legendReturning) {
        return {
          position: "fixed",
          left: this.legendBaseLeft + "px",
          top: this.legendBaseBottom - 44 + "px",
          bottom: "auto",
          right: "auto",
          zIndex: 9999,
          transition: "left 0.35s ease, top 0.35s ease",
          cursor: "grab",
        };
      }
      return {};
    },
  },
  methods: {
    formatStockNum(val) {
      if (val === null || val === undefined || val === "") return "-";
      const num = parseFloat(val);
      if (isNaN(num)) return val;
      if (Number.isInteger(num)) return num.toString();
      return num.toString();
    },
    getUnplacedCountByType(typeValue) {
      return this.unplacedLocations.filter(
        (u) => (u.wareType ?? u.ware_type ?? 2) === typeValue,
      ).length;
    },
    startRenderChunks() {
      if (this.renderTimer) cancelAnimationFrame(this.renderTimer);
      this.renderedCount = 0;
      const renderNext = () => {
        if (this.renderedCount < this.inventoryData.length) {
          this.renderedCount = Math.min(
            this.renderedCount + this.renderChunkSize,
            this.inventoryData.length,
          );
          this.renderTimer = requestAnimationFrame(renderNext);
        }
      };
      renderNext();
    },
    cancelHighlight() {
      this.highlightCancelled = true;
    },
    async mobileHighlightAll() {
      const mats = this.searchMaterialResults;
      if (!mats.length || this.highlightRunning) return;
      this.showHeatmap = false;
      this.highlightRunning = true;
      this.highlightCancelled = false;
      this.highlightedLocations = new Set();
      const total = mats.length;
      for (let i = 0; i < total; i++) {
        if (this.highlightCancelled) break;
        this.highlightProgress = `点亮中 ${i + 1}/${total}`;
        const m = mats[i];
        try {
          const params = {
            stockId: this.selectedWareId,
            warehouseId: this.selectedWareId,
          };
          if (m.materialId) params.materialId = m.materialId;
          else params.materialNo = m.materialNo;
          const res = await request.get(
            "/api_hf/uic-system/ware/location/detail",
            { params },
          );
          const payload = res?.data || [];
          const rows = Array.isArray(payload)
            ? payload
            : payload
            ? [payload]
            : [];
          if (rows.length > 0) {
            const locName = rows[0].locationNo || rows[0].locationName || "";
            if (locName && !this.highlightedLocations.has(locName)) {
              this.highlightedLocations.add(locName);
            }
          }
        } catch (_) {
          void 0;
        }
        await new Promise((r) => setTimeout(r, 20));
      }
      const count = this.highlightedLocations.size;
      this.highlightProgress = "";
      this.highlightRunning = false;
      if (count) {
        this.$nextTick(() => this.drawAllPallets());
        this.myToast(`已高亮 ${count} 个库位`, "success");
      } else {
        this.myToast("所搜物料的库位未在画布上摆放", "error");
      }
    },
    async mobileHighlightSingle(m) {
      this.highlightRunning = true;
      this.highlightProgress = "点亮中...";
      this.showHeatmap = false;
      try {
        const params = {
          stockId: this.selectedWareId,
          warehouseId: this.selectedWareId,
        };
        if (m.materialId) params.materialId = m.materialId;
        else params.materialNo = m.materialNo;
        const res = await request.get(
          "/api_hf/uic-system/ware/location/detail",
          { params },
        );
        const payload = res?.data || [];
        const rows = Array.isArray(payload)
          ? payload
          : payload
          ? [payload]
          : [];
        if (rows.length > 0) {
          const locName = rows[0].locationNo || rows[0].locationName || "";
          if (locName) {
            this.highlightedLocations = new Set([locName]);
            this.$nextTick(() => this.drawAllPallets());
            this.myToast(`已高亮库位: ${locName}`, "success");
          } else {
            this.myToast("该物料未找到库位", "error");
          }
        } else {
          this.myToast("该物料未找到库位", "error");
        }
      } catch (_) {
        this.myToast("查询失败", "error");
      } finally {
        this.highlightProgress = "";
        this.highlightRunning = false;
      }
    },
    async mobileHighlightAllSupplier() {
      const mats = this.searchMaterials;
      if (!mats.length || this.highlightRunning) return;
      this.showHeatmap = false;
      this.highlightRunning = true;
      this.highlightCancelled = false;
      this.highlightedLocations = new Set();
      const total = mats.length;
      for (let i = 0; i < total; i++) {
        if (this.highlightCancelled) break;
        this.highlightProgress = `点亮中 ${i + 1}/${total}`;
        const m = mats[i];
        try {
          const params = {
            stockId: this.selectedWareId,
            warehouseId: this.selectedWareId,
          };
          if (m.materialId) params.materialId = m.materialId;
          else params.materialNo = m.materialNo;
          const res = await request.get(
            "/api_hf/uic-system/ware/location/detail",
            { params },
          );
          const payload = res?.data || [];
          const rows = Array.isArray(payload)
            ? payload
            : payload
            ? [payload]
            : [];
          if (rows.length > 0) {
            const locName = rows[0].locationNo || rows[0].locationName || "";
            if (locName && !this.highlightedLocations.has(locName)) {
              this.highlightedLocations.add(locName);
            }
          }
        } catch (_) {
          void 0;
        }
        await new Promise((r) => setTimeout(r, 20));
      }
      const count = this.highlightedLocations.size;
      this.highlightProgress = "";
      this.highlightRunning = false;
      if (count) {
        this.$nextTick(() => this.drawAllPallets());
        this.myToast(`已高亮 ${count} 个库位`, "success");
      } else {
        this.myToast("所搜物料的库位未在画布上摆放", "error");
      }
    },
    handleCanvasClick() {
      if (this.showSearchPanel) {
        this.showSearchPanel = false;
      }
    },
    // ── 热力图图例拖动 ──
    onLegendDragStart(e) {
      if (this.editMode) return;
      const el = this.$refs.legendRef;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // 记录原位（视口坐标）
      this.legendBaseLeft = rect.left;
      this.legendBaseBottom = rect.bottom;
      this.legendStartX = e.clientX;
      this.legendStartY = e.clientY;
      this.legendOffsetX = 0;
      this.legendOffsetY = 0;
      this.legendPointerId = e.pointerId;
      this.legendReturning = false;
      this.legendDragging = true;
      try {
        el.setPointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }
    },
    onLegendDragMove(e) {
      if (!this.legendDragging) return;
      if (e.pointerId !== this.legendPointerId) return;
      this.legendOffsetX = e.clientX - this.legendStartX;
      this.legendOffsetY = e.clientY - this.legendStartY;
    },
    onLegendDragEnd(e) {
      if (!this.legendDragging) return;
      if (e.pointerId != null && e.pointerId !== this.legendPointerId) return;
      try {
        if (this.$refs.legendRef && this.legendPointerId != null) {
          this.$refs.legendRef.releasePointerCapture(this.legendPointerId);
        }
      } catch (_) {
        /* ignore */
      }
      this.legendDragging = false;
      this.legendPointerId = null;
      this.legendOffsetX = 0;
      this.legendOffsetY = 0;
      // 启动平滑返航
      this.legendReturning = true;
      // 350ms 后清除返航状态，样式由 CSS 控制
      setTimeout(() => {
        this.legendReturning = false;
      }, 360);
    },
    isSupplierCollapsed(name) {
      const key = String(name || "").trim() || "未知供应商";
      return this.supplierCollapsed?.[key] !== false;
    },
    toggleSupplierCollapse(name) {
      const key = String(name || "").trim() || "未知供应商";
      const next = !this.isSupplierCollapsed(key);
      this.supplierCollapsed = {
        ...(this.supplierCollapsed || {}),
        [key]: next,
      };
    },
    computeSnapForDrag(item, nx, ny) {
      const threshold = 8;
      const others = (
        Array.isArray(this.inventoryData) ? this.inventoryData : []
      ).filter((o) => o && o.id !== item.id);
      const itemW = Number(item?.w) || 0;
      const itemH = Number(item?.h) || 0;

      let bestDx = null;
      let bestDy = null;
      const guidesV = [];
      const guidesH = [];

      const xCandidates = [nx, nx + itemW];
      const yCandidates = [ny, ny + itemH];

      for (const o of others) {
        const ox = Number(o?.x) || 0;
        const oy = Number(o?.y) || 0;
        const ow = Number(o?.w) || 0;
        const oh = Number(o?.h) || 0;
        const oXEdges = [ox, ox + ow];
        const oYEdges = [oy, oy + oh];

        for (const xc of xCandidates) {
          for (const xe of oXEdges) {
            const dx = xe - xc;
            if (
              Math.abs(dx) <= threshold &&
              (bestDx == null || Math.abs(dx) < Math.abs(bestDx))
            ) {
              bestDx = dx;
              guidesV.length = 0;
              guidesV.push(xe);
            }
          }
        }
        for (const yc of yCandidates) {
          for (const ye of oYEdges) {
            const dy = ye - yc;
            if (
              Math.abs(dy) <= threshold &&
              (bestDy == null || Math.abs(dy) < Math.abs(bestDy))
            ) {
              bestDy = dy;
              guidesH.length = 0;
              guidesH.push(ye);
            }
          }
        }
      }

      const outX = bestDx != null ? nx + bestDx : nx;
      const outY = bestDy != null ? ny + bestDy : ny;
      return { x: outX, y: outY, guidesV, guidesH };
    },
    computeSnapForResize(item, w, h) {
      const threshold = 8;
      const others = (
        Array.isArray(this.inventoryData) ? this.inventoryData : []
      ).filter((o) => o && o.id !== item.id);
      const x = Number(item?.x) || 0;
      const y = Number(item?.y) || 0;
      const right = x + w;
      const bottom = y + h;

      let bestDw = null;
      let bestDh = null;
      const guidesV = [];
      const guidesH = [];

      for (const o of others) {
        const ox = Number(o?.x) || 0;
        const oy = Number(o?.y) || 0;
        const ow = Number(o?.w) || 0;
        const oh = Number(o?.h) || 0;
        const oXEdges = [ox, ox + ow];
        const oYEdges = [oy, oy + oh];

        for (const xe of oXEdges) {
          const dw = xe - right;
          if (
            Math.abs(dw) <= threshold &&
            (bestDw == null || Math.abs(dw) < Math.abs(bestDw))
          ) {
            bestDw = dw;
            guidesV.length = 0;
            guidesV.push(xe);
          }
        }
        for (const ye of oYEdges) {
          const dh = ye - bottom;
          if (
            Math.abs(dh) <= threshold &&
            (bestDh == null || Math.abs(dh) < Math.abs(bestDh))
          ) {
            bestDh = dh;
            guidesH.length = 0;
            guidesH.push(ye);
          }
        }
      }

      const outW = bestDw != null ? w + bestDw : w;
      const outH = bestDh != null ? h + bestDh : h;
      return { w: outW, h: outH, guidesV, guidesH };
    },
    toggleMobileSearch() {
      this.mobileSearchOpen = !this.mobileSearchOpen;
    },
    handleViewportPointerDown(e) {
      if (!this.isMobile) return;
      if (e.pointerType !== "touch") return;

      const el = this.$refs.warehouseViewport;
      if (!el) return;

      this.viewportPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.viewportPointers.size === 2) {
        try {
          e.preventDefault();
        } catch (_) {
          void 0;
        }
        try {
          el.setPointerCapture(e.pointerId);
        } catch (_) {
          void 0;
        }

        this.panPointerId = null;
        this.panPending = null;
        if (this.panRaf) {
          cancelAnimationFrame(this.panRaf);
          this.panRaf = 0;
        }

        const rect = el.getBoundingClientRect();
        const pts = Array.from(this.viewportPointers.values());
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        this.pinchStartDist = Math.hypot(dx, dy) || 1;
        // 移动端：记录画布基准尺寸用于捏合缩放
        this.pinchStartScale = this.userScale;

        const centerX = (pts[0].x + pts[1].x) / 2;
        const centerY = (pts[0].y + pts[1].y) / 2;
        this.pinchStartCenterX = centerX - rect.left;
        this.pinchStartCenterY = centerY - rect.top;

        const cs = window.getComputedStyle(el);
        const padLeft = Number.parseFloat(cs.paddingLeft || "0") || 0;
        const padTop = Number.parseFloat(cs.paddingTop || "0") || 0;
        this.pinchPadLeft = padLeft;
        this.pinchPadTop = padTop;

        this.pinchStartScrollLeft = el.scrollLeft;
        this.pinchStartScrollTop = el.scrollTop;
        this.pinchStartEffectiveScale = Number(this.effectiveScale) || 1;
      }
    },
    handleViewportPointerMove(e) {
      if (!this.isMobile) return;
      if (e.pointerType !== "touch") return;
      if (!this.viewportPointers.has(e.pointerId)) return;

      this.viewportPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const el = this.$refs.warehouseViewport;
      if (!el) return;

      if (this.viewportPointers.size !== 2) return;

      try {
        e.preventDefault();
      } catch (_) {
        void 0;
      }

      const pts = Array.from(this.viewportPointers.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;
      const next = this.pinchStartScale * (dist / (this.pinchStartDist || 1));
      const clamped = Math.max(
        this.minUserScale,
        Math.min(this.maxUserScale, next),
      );

      this.pinchPendingScale = clamped;
      if (this.pinchRaf) return;
      this.pinchRaf = requestAnimationFrame(() => {
        this.pinchRaf = 0;
        if (this.pinchPendingScale == null) return;
        const nextUserScale = this.pinchPendingScale;
        const nextEffectiveScale = (Number(this.fitScale) || 1) * nextUserScale;
        const startEffectiveScale = Number(this.pinchStartEffectiveScale) || 1;

        const focusX =
          (this.pinchStartScrollLeft +
            this.pinchStartCenterX -
            this.pinchPadLeft) /
          startEffectiveScale;
        const focusY =
          (this.pinchStartScrollTop +
            this.pinchStartCenterY -
            this.pinchPadTop) /
          startEffectiveScale;

        this.userScale = nextUserScale;
        this.$nextTick(() => {
          const x =
            focusX * nextEffectiveScale -
            this.pinchStartCenterX +
            this.pinchPadLeft;
          const y =
            focusY * nextEffectiveScale -
            this.pinchStartCenterY +
            this.pinchPadTop;
          el.scrollLeft = x;
          el.scrollTop = y;
        });
        this.pinchPendingScale = null;
      });
    },
    handleViewportPointerUp(e) {
      if (!this.isMobile) return;
      if (e.pointerType !== "touch") return;
      if (!this.viewportPointers.has(e.pointerId)) return;

      this.viewportPointers.delete(e.pointerId);

      if (this.panPointerId === e.pointerId) {
        this.panPointerId = null;
        this.panPending = null;
        if (this.panRaf) {
          cancelAnimationFrame(this.panRaf);
          this.panRaf = 0;
        }
      }

      if (this.viewportPointers.size < 2) {
        this.pinchStartDist = 0;
        this.pinchStartScale = this.userScale;
      }
    },
    normalizeLocationDetailPayload(payload) {
      if (!payload) return null;
      if (!Array.isArray(payload)) return payload;
      if (payload.length === 0) return null;

      const first = payload[0] || {};
      const mergedMaterials = payload.flatMap((x) =>
        Array.isArray(x?.materials) ? x.materials : [],
      );

      return {
        stockId: first.stockId,
        warehouseId: first.warehouseId,
        locationId: first.locationId,
        locationNo: first.locationNo,
        locationName: first.locationName,
        supplierId: first.supplierId,
        supplierName: first.supplierName,
        totalStockNum: first.totalStockNum,
        materials: mergedMaterials,
      };
    },
    getSupplierGroups(detail) {
      const materials = Array.isArray(detail?.materials)
        ? detail.materials
        : [];
      const groups = new Map();
      for (const m of materials) {
        const name =
          m?.supplierName && String(m.supplierName).trim()
            ? String(m.supplierName).trim()
            : "未知供应商";
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(m);
      }
      return Array.from(groups.entries()).map(([supplierName, items]) => ({
        supplierName,
        items,
      }));
    },
    getSupplierDisplay(detail) {
      if (!detail) return "-";

      const suppliers = Array.isArray(detail.suppliers) ? detail.suppliers : [];
      if (suppliers.length > 0) {
        const names = suppliers.map((s) => s?.supplierName).filter(Boolean);
        return names.length > 0 ? Array.from(new Set(names)).join("、") : "-";
      }

      if (detail.supplierName) return String(detail.supplierName);

      const materials = Array.isArray(detail.materials) ? detail.materials : [];
      const matNames = materials.map((m) => m?.supplierName).filter(Boolean);
      return matNames.length > 0
        ? Array.from(new Set(matNames)).join("、")
        : "-";
    },
    toggleEdit() {
      if (this.isMobile) return;
      this.editMode = !this.editMode;
      this.selectedBatchItems = [];
      this.selectedLocationKey = "";
      if (!this.editMode) {
        this.highlightedLocations = new Set();
        this.editSearchPrefix = "";
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    snapToGridValue(value) {
      if (!this.snapToGrid) return value;
      return Math.round(value / this.gridSize) * this.gridSize;
    },
    clampToWarehouse(nx, ny, w, h) {
      let x = nx;
      let y = ny;
      x = Math.max(this.warehouse.minX, Math.min(this.warehouse.maxX - w, x));
      y = Math.max(this.warehouse.minY, Math.min(this.warehouse.maxY - h, y));
      x = this.snapToGridValue(x);
      y = this.snapToGridValue(y);
      return { x, y };
    },
    async fetchWareList() {
      if (!this.bizDept) return;
      if (this.wareLoading) return;
      this.wareLoading = true;
      try {
        const res = await request.get("/uic-system/ware/list", {
          params: {
            wareType: 0,
            bizDept: this.bizDept,
          },
        });

        const raw = res?.data;
        const list = raw?.records || raw?.list || raw?.data || raw || [];
        this.wareList = Array.isArray(list) ? list : [];
        if (!this.selectedWareId && this.wareList.length > 0) {
          this.selectedWareId = this.wareList[0].id;
          await this.fetchLayout();
          // 静默加载供应商数据（不打开面板）
          this.fetchSuppliersSilent();
        }
      } catch (e) {
        this.wareList = [];
        this.selectedWareId = "";
        console.error("获取仓库失败:", e);
      } finally {
        this.wareLoading = false;
      }
    },
    normalizeLayoutResponse(payload) {
      const baseCanvasWidth = Number(payload?.baseCanvasWidth) || 1200;
      const baseCanvasHeight = Number(payload?.baseCanvasHeight) || 680;

      let placed = [];
      let unplaced = [];

      if (Array.isArray(payload?.placed) || Array.isArray(payload?.unplaced)) {
        placed = Array.isArray(payload?.placed) ? payload.placed : [];
        unplaced = Array.isArray(payload?.unplaced) ? payload.unplaced : [];
        console.log("[Dashboard] 使用布局数据结构");
      } else {
        const locations =
          payload?.records || payload?.list || payload?.data || [];
        console.log(
          "[Dashboard] 使用分页数据结构，总库位数:",
          locations.length,
        );

        locations.forEach((location) => {
          const hasCoords =
            location.x != null &&
            location.y != null &&
            location.width != null &&
            location.height != null;
          if (hasCoords) {
            placed.push(location);
          } else {
            unplaced.push(location);
          }
        });
      }

      console.log("[Dashboard] 解析布局数据:", {
        baseCanvasWidth,
        baseCanvasHeight,
        placedCount: placed.length,
        unplacedCount: unplaced.length,
        firstPlaced: placed[0],
        firstUnplaced: unplaced[0],
      });

      const src = placed;

      const pickId = (it) => it.id || it.locationId || it.wareId || "";
      const toName = (it) =>
        it.locationNo || it.wareNo || it.locationName || it.wareName || "";
      const pickX = (it) => it.x ?? it.posX ?? it.left;
      const pickY = (it) => it.y ?? it.posY ?? it.top;
      const pickW = (it) => it.width ?? it.w;
      const pickH = (it) => it.height ?? it.h;

      const mapPlaced = (it) => {
        const apiW = Number(pickW(it)) || 0;
        const apiH = Number(pickH(it)) || 0;
        const occ = Math.max(
          0,
          Number(it.wareOccupancy || it.ware_occupancy) || 0,
        );
        const wareType = it.wareType ?? it.ware_type ?? 2;
        let pw, ph, w, h;
        if (wareType === 2) {
          pw = Math.max(0.5, Number(it.palletWeight || it.pallet_weight) || 1);
          ph = Math.max(0.5, Number(it.palletHeight || it.pallet_height) || 1);
          w = pw * this.baseCellW;
          h = ph * this.baseCellH;
        } else {
          pw = apiW ? Math.round(apiW / this.baseCellW) || 1 : 1;
          ph = apiH ? Math.round(apiH / this.baseCellH) || 1 : 1;
          w = apiW || this.baseCellW;
          h = apiH || this.baseCellH;
        }
        return {
          id: pickId(it),
          name: toName(it),
          x: Number(pickX(it)),
          y: Number(pickY(it)),
          z: Number(it.z || 1),
          w,
          h,
          palletWeight: pw,
          palletHeight: ph,
          occupancy: occ,
          wareType,
        };
      };
      const mapUnplaced = (it) => {
        const wt = it.wareType ?? it.ware_type ?? 2;
        const apiW = Number(it.width ?? it.w) || 0;
        const apiH = Number(it.height ?? it.h) || 0;
        let pw = it.palletWeight ?? it.pallet_weight ?? null;
        let ph = it.palletHeight ?? it.pallet_height ?? null;
        console.log(
          "[mapUnplaced]",
          it.locationNo || it.name,
          "API palletWeight:",
          it.palletWeight,
          "pallet_weight:",
          it.pallet_weight,
          "=> pw:",
          pw,
        );
        if (pw == null || ph == null) {
          pw = apiW ? Math.round(apiW / this.baseCellW) || 1 : 1;
          ph = apiH ? Math.round(apiH / this.baseCellH) || 1 : 1;
        } else {
          pw = Number(pw) || 1;
          ph = Number(ph) || 1;
        }
        return {
          id: pickId(it),
          name: toName(it),
          remainPallet: it.remainPallet ?? null,
          maxPallet: it.maxPallet ?? null,
          pallet_weight: pw,
          pallet_height: ph,
          wareType: wt,
          width: it.width ?? it.w ?? null,
          height: it.height ?? it.h ?? null,
        };
      };

      const mappedPlaced = src
        .filter(
          (it) =>
            it &&
            pickX(it) != null &&
            pickY(it) != null &&
            pickW(it) != null &&
            pickH(it) != null,
        )
        .map(mapPlaced)
        .filter(
          (it) =>
            Number.isFinite(it.x) &&
            Number.isFinite(it.y) &&
            Number.isFinite(it.w) &&
            Number.isFinite(it.h),
        );

      const mappedUnplaced = (unplaced.length ? unplaced : src)
        .filter(
          (it) =>
            it &&
            (pickX(it) == null ||
              pickY(it) == null ||
              pickW(it) == null ||
              pickH(it) == null),
        )
        .map(mapUnplaced)
        .filter((it) => it.id);

      return {
        baseCanvasWidth,
        baseCanvasHeight,
        inventoryData: mappedPlaced,
        unplacedLocations: mappedUnplaced,
      };
    },
    async fetchLayout() {
      if (!this.bizDept || !this.selectedWareId) return;
      if (this.layoutLoading) return;
      this.layoutLoading = true;
      try {
        const res = await request.get("/api_hf/uic-system/ware/page", {
          params: {
            bizDept: this.bizDept,
            parentId: this.selectedWareId,
            current: 1,
            size: 500,
          },
        });

        const payload = res?.data || {};

        console.log("[Dashboard] 库位接口响应:", payload);

        const normalized = this.normalizeLayoutResponse(payload);
        console.log("[Dashboard] layout normalized", {
          warehouseId: this.selectedWareId,
          placed: normalized.inventoryData.length,
          unplaced: normalized.unplacedLocations.length,
        });
        this.baseCanvasWidth = normalized.baseCanvasWidth;
        this.baseCanvasHeight = normalized.baseCanvasHeight;
        this.inventoryData = normalized.inventoryData;
        this.unplacedLocations = normalized.unplacedLocations;
        this.originalData = JSON.parse(JSON.stringify(this.inventoryData));
        this.startRenderChunks();
        this.updateCanvasSize();
        this.updateFitScale();
        this.fitDone = false;
        this.$nextTick(() => {
          if (!this.fitDone) {
            this.fitDone = true;
            this.fitToContent();
          }
        });
      } catch (e) {
        console.error("获取库位布局失败:", e);
        this.inventoryData = [];
        this.originalData = [];
        this.startRenderChunks();
        this.unplacedLocations = [];
        this.layoutRawPlacedCount = 0;
        this.layoutRawUnplacedCount = 0;
      } finally {
        this.layoutLoading = false;
      }
    },
    async handleWareChange() {
      if (this.editMode) {
        const ok = await this.myConfirm(
          "切换仓库",
          "切换仓库将退出编辑模式，未保存的更改将丢失。确定要切换吗？",
        );
        if (!ok) return;
        this.editMode = false;
      }
      this.searchKey = "";
      this.searchResults = [];
      this.selectedLocationKey = "";
      this.selectedMaterialNo = "";
      this.showDialog = false;
      this.currentItem = null;
      this.inventoryData = [];
      this.originalData = [];
      this.startRenderChunks();
      this.fitDone = false;
      this.fetchLayout();
      this.fetchSuppliersSilent(); // 静默加载供应商数据
    },
    handleUnplacedDragStart(e, u) {
      if (!this.editMode) {
        try {
          e.preventDefault();
        } catch (_) {
          void 0;
        }
        return;
      }

      this.draggingUnplacedId = u.id;

      try {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", u.id);
        e.dataTransfer.setData("text", u.id);
        e.dataTransfer.setData("Text", u.id);
        e.dataTransfer.setData("application/json", JSON.stringify(u));
      } catch (error) {
        console.warn("[Dashboard] 拖拽数据设置失败:", error);
        this.draggedUnplacedItem = u;
      }

      try {
        e.dataTransfer.setDragImage(e.target, 0, 0);
      } catch (error) {
        console.warn("[Dashboard] 设置拖拽图像失败:", error);
      }
    },
    handleUnplacedDragEnd(e) {
      this.draggingUnplacedId = "";
      this.draggedUnplacedItem = null;

      try {
        e.dataTransfer.clearData();
      } catch (_) {
        void 0;
      }
    },
    handleUnplacedDblClick(u) {
      if (!this.editMode) return;
      if (!u || !u.id) return;

      const idx = this.unplacedLocations.findIndex((x) => x.id === u.id);
      if (idx < 0) return;

      const defaultW = 100;
      const defaultH = 50;
      const offset = (this.inventoryData.length % 12) * (this.gridSize * 3);
      const baseX = this.warehouse.minX + this.gridSize * 2;
      const baseY = this.warehouse.minY + this.gridSize * 2;
      const pos = this.clampToWarehouse(
        baseX + offset,
        baseY + offset,
        defaultW,
        defaultH,
      );

      if (this.checkOverlap(null, defaultW, defaultH, pos.x, pos.y, u.id))
        return;

      const placedItem = {
        id: u.id,
        name: u.name || u.locationNo || "",
        fullName: u.fullName || u.locationName || "",
        materialNo: u.materialNo,
        quantity: u.quantity,
        x: pos.x,
        y: pos.y,
        z: u.z ?? 1,
        w: defaultW,
        h: defaultH,
      };

      this.inventoryData.push(placedItem);
      this.unplacedLocations.splice(idx, 1);
      this.updateCanvasSize();
      this.startRenderChunks();
    },
    async loadAreaSuppliersData() {
      try {
        const res = await request.get("/uic-system/supplier/page", {
          params: { current: 1, size: 200 },
        });
        const payload = res?.data || {};
        const list = payload?.records || payload?.list || payload?.data || [];
        const suppliers = Array.isArray(list) ? list : [];
        let totalRent = 0,
          totalOccupy = 0,
          hasData = false;
        this.areaSuppliers = suppliers
          .map((s) => {
            const rent = Number(s.rentArea) || 0;
            const occupy = Number(s.occupyArea) || 0;
            const rawRate =
              s.occupyRate != null
                ? Number(s.occupyRate)
                : rent > 0
                ? occupy / rent
                : null;
            const rate = rawRate != null ? Math.round(rawRate * 100) : null;
            if (rent > 0 || occupy > 0) hasData = true;
            totalRent += rent;
            totalOccupy += occupy;
            return {
              supplierName: s.supplierName || s.name || "",
              supplierId: s.id || s.supplierId,
              deptName: s.bizDeptName || s.deptName || "",
              rentArea: rent || null,
              occupyArea: occupy || null,
              occupyRate: rate,
            };
          })
          .sort((a, b) => (b.occupyRate || 0) - (a.occupyRate || 0));
        this.areaTotal = hasData
          ? {
              rentArea: totalRent,
              occupyArea: totalOccupy,
              occupyRate:
                totalRent > 0
                  ? Math.round((totalOccupy / totalRent) * 100)
                  : null,
            }
          : { rentArea: null, occupyArea: null, occupyRate: null };
      } catch (e) {
        this.areaSuppliers = [];
        this.areaTotal = { rentArea: null, occupyArea: null, occupyRate: null };
      }
    },
    async fetchSuppliersSilent() {
      // 静默加载供应商数据（不打开面板、不显示 loading）供底部概况栏使用
      if (this.areaSuppliers && this.areaSuppliers.length) return;
      await this.loadAreaSuppliersData();
    },
    async openAreaPanel(force) {
      this.showAreaPanel = true;
      if (!force && this.areaSuppliers.length) return;
      this.areaLoading = true;
      await this.loadAreaSuppliersData();
      this.areaLoading = false;
    },
    openSupplierTrend(s) {
      if (!s || !s.supplierId) {
        this.myToast("缺少供应商ID", "error");
        return;
      }
      this.trendSupplier = s;
      this.trendError = "";
      this.trendPoints = [];
      this.trendStats = null;
      // 默认最近 30 天
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };
      this.trendStartDate = fmt(start);
      this.trendEndDate = fmt(end);
      this.supplierTrendVisible = true;
      this.$nextTick(() => this.loadSupplierTrend());
    },
    closeSupplierTrend() {
      this.supplierTrendVisible = false;
      if (this.trendChartArea) {
        this.trendChartArea.dispose();
        this.trendChartArea = null;
      }
      if (this.trendChartRate) {
        this.trendChartRate.dispose();
        this.trendChartRate = null;
      }
      if (this._trendResizeHandler) {
        window.removeEventListener("resize", this._trendResizeHandler);
        this._trendResizeHandler = null;
      }
    },
    async loadSupplierTrend() {
      const s = this.trendSupplier;
      if (!s || !s.supplierId) return;
      if (!this.trendStartDate || !this.trendEndDate) {
        this.trendError = "请选择开始和结束日期";
        return;
      }
      if (this.trendStartDate > this.trendEndDate) {
        this.trendError = "开始日期不能晚于结束日期";
        return;
      }
      this.trendLoading = true;
      this.trendError = "";
      this.trendPoints = [];
      this.trendStats = null;
      try {
        // 后端接口：GET /uic-system_hf/supplierAreaDaily/page
        // 参数：supplierId、bizDept、startDate、endDate、current=1、size=100
        // 返回：R<IPage<{ recordDate, rentArea, occupyArea, occupyRate, supplierId, supplierName }>>
        const res = await request.get("uic-system_hf/supplierAreaDaily/page", {
          params: {
            supplierId: s.supplierId,
            bizDept: this.bizDept || "",
            startDate: this.trendStartDate,
            endDate: this.trendEndDate,
            current: 1,
            size: 100,
          },
        });
        const rows = (res && (res.data || res)) || [];
        const list = Array.isArray(rows)
          ? rows
          : Array.isArray(rows.records)
          ? rows.records
          : [];
        if (!list.length) {
          this.trendError = "该时间范围内暂无占用数据";
          return;
        }
        const points = this.parseAreaDailyList(list);
        if (!points.length) {
          this.trendError = "该时间范围内暂无占用数据";
          return;
        }
        this.trendPoints = points;
        // 必须 await 下一轮 nextTick，让 v-if/v-else 分支中 <div ref="supplierTrendChart"> 真正进 DOM
        await this.$nextTick();
        this.renderTrendChart(points);
      } catch (e) {
        console.error("供应商占用趋势加载失败:", e);
        this.trendError = e?.message || "加载失败，请稍后重试";
      } finally {
        this.trendLoading = false;
      }
    },
    parseAreaDailyList(rows) {
      if (!rows || !rows.length) return [];
      // 自适应识别列名：优先精确匹配，其次关键词匹配
      const header = Object.keys(rows[0] || {});
      const findCol = (kws) => {
        // 1. 精确匹配（区分大小写）
        for (const h of header) if (kws.some((k) => h === k)) return h;
        // 2. 不区分大小写精确匹配
        for (const h of header) {
          const lower = String(h).toLowerCase();
          if (kws.some((k) => lower === k.toLowerCase())) return h;
        }
        // 3. 关键词包含匹配（仅当前缀/结尾/下划线分段匹配，避免匹配到 updateTime 中的 date）
        for (const h of header) {
          const lower = String(h).toLowerCase();
          if (
            kws.some(
              (k) =>
                lower === k.toLowerCase() ||
                lower.startsWith(k.toLowerCase() + "_") ||
                lower.endsWith("_" + k.toLowerCase()),
            )
          )
            return h;
        }
        return null;
      };
      const dateCol = findCol([
        "recordDate",
        "record_date",
        "RecordDate",
        "日期",
      ]);
      const areaCol = findCol([
        "occupyArea",
        "occupy_area",
        "OccupyArea",
        "占用面积",
      ]);
      const rentCol = findCol([
        "rentArea",
        "rent_area",
        "RentArea",
        "租赁面积",
      ]);
      const rateCol = findCol([
        "occupyRate",
        "occupy_rate",
        "OccupyRate",
        "占用率",
        "占有率",
      ]);
      if (!dateCol) return [];
      const dateMap = {};
      rows.forEach((r) => {
        const rawDate = r[dateCol];
        if (rawDate === null || rawDate === undefined || rawDate === "") return;
        let dateStr = String(rawDate);
        const m = dateStr.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
        if (m)
          dateStr = `${m[1]}-${String(m[2]).padStart(2, "0")}-${String(
            m[3],
          ).padStart(2, "0")}`;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return;
        // 按天保留三个指标的最新值（同一天多条取 max + 累加 count）
        if (!dateMap[dateStr])
          dateMap[dateStr] = {
            rentArea: 0,
            occupyArea: 0,
            occupyRate: 0,
            count: 0,
          };
        const rent = rentCol ? Number(r[rentCol]) || 0 : 0;
        const area = areaCol ? Number(r[areaCol]) || 0 : 0;
        const rate = rateCol ? Number(r[rateCol]) || 0 : 0;
        // rentArea/occupyArea 同一天多条取均值（累加 / count）
        dateMap[dateStr].rentArea += rent;
        dateMap[dateStr].occupyArea += area;
        dateMap[dateStr].count += 1;
        // occupyRate 取当日最后一次（定时任务一般每天 1 条，取最大）
        if (rate > dateMap[dateStr].occupyRate)
          dateMap[dateStr].occupyRate = rate;
      });
      return Object.keys(dateMap)
        .sort()
        .map((d) => {
          const c = dateMap[d].count || 1;
          return {
            date: d,
            rentArea: Math.round((dateMap[d].rentArea / c) * 100) / 100,
            occupyArea: Math.round((dateMap[d].occupyArea / c) * 100) / 100,
            occupyRate: dateMap[d].occupyRate,
            count: c,
          };
        });
    },
    renderTrendChart(points) {
      if (this.trendChartArea) {
        this.trendChartArea.dispose();
        this.trendChartArea = null;
      }
      if (this.trendChartRate) {
        this.trendChartRate.dispose();
        this.trendChartRate = null;
      }
      let retry = 0;
      const tryInit = () => {
        const elArea = this.$refs.trendChartArea;
        const elRate = this.$refs.trendChartRate;
        if (!elArea || !elRate) {
          if (retry++ < 5) {
            this.$nextTick(tryInit);
          } else {
            console.warn("[趋势图表] ref 未拿到元素");
          }
          return;
        }
        this._doInitTrendCharts(elArea, elRate, points);
      };
      this.$nextTick(tryInit);
    },
    _doInitTrendCharts(elArea, elRate, points) {
      // 计算统计
      const rates = points.map((p) => p.occupyRate || 0);
      const rents = points.map((p) => p.rentArea || 0);
      const occupies = points.map((p) => p.occupyArea || 0);
      this.trendStats = {
        recordCount: points.reduce((a, p) => a + p.count, 0),
        maxRate: Math.round(Math.max(...rates) * 10) / 10,
        avgRate:
          Math.round((rates.reduce((a, b) => a + b, 0) / rates.length) * 10) /
          10,
        maxRent: Math.round(Math.max(...rents) * 100) / 100,
        avgRent:
          Math.round((rents.reduce((a, b) => a + b, 0) / rents.length) * 100) /
          100,
        maxOccupy: Math.round(Math.max(...occupies) * 100) / 100,
        avgOccupy:
          Math.round(
            (occupies.reduce((a, b) => a + b, 0) / occupies.length) * 100,
          ) / 100,
      };
      const xData = points.map((p) => p.date.slice(5));
      // 上图：面积（租用+已用）
      const chartArea = echarts.init(elArea, null, { renderer: "canvas" });
      chartArea.setOption({
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            const date = params[0].axisValueLabel;
            const fullDate =
              points.find((p) => p.date.endsWith(date))?.date || date;
            const rent = params.find((x) => x.seriesName === "租用面积");
            const occ = params.find((x) => x.seriesName === "已用面积");
            const lines = [`<b>${fullDate}</b>`];
            if (rent) lines.push(`租用面积：${rent.value} m²`);
            if (occ) lines.push(`已用面积：${occ.value} m²`);
            return lines.join("<br/>");
          },
        },
        legend: {
          top: 4,
          right: 12,
          data: ["租用面积", "已用面积"],
          textStyle: { fontSize: 11, color: "#374151" },
          itemWidth: 18,
          itemHeight: 8,
        },
        grid: { left: 56, right: 20, top: 32, bottom: 20, containLabel: true },
        xAxis: {
          type: "category",
          data: xData,
          axisLabel: { fontSize: 11, color: "#374151" },
          axisLine: { lineStyle: { color: "#000000" } },
          boundaryGap: false,
        },
        yAxis: {
          type: "value",
          name: "面积(m²)",
          nameTextStyle: { fontSize: 11, color: "#374151" },
          axisLabel: {
            fontSize: 11,
            color: "#374151",
            formatter: (v) => (v >= 1000 ? (v / 1000).toFixed(1) + "k" : v),
          },
          splitLine: { lineStyle: { color: "#e5e7eb" } },
          axisLine: { lineStyle: { color: "#000000" } },
        },
        series: [
          {
            name: "租用面积",
            type: "line",
            data: rents,
            smooth: true,
            symbol: "square",
            symbolSize: 6,
            lineStyle: { width: 2, color: "#1d4ed8", type: "dashed" },
            itemStyle: { color: "#1d4ed8" },
          },
          {
            name: "已用面积",
            type: "line",
            data: occupies,
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            lineStyle: { width: 2, color: "#16a34a" },
            itemStyle: { color: "#16a34a" },
            areaStyle: { color: "rgba(22,163,74,0.12)" },
          },
        ],
      });
      requestAnimationFrame(() => chartArea.resize());
      this.trendChartArea = chartArea;
      // 下图：占用率 0-100%
      const chartRate = echarts.init(elRate, null, { renderer: "canvas" });
      chartRate.setOption({
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            const date = params[0].axisValueLabel;
            const fullDate =
              points.find((p) => p.date.endsWith(date))?.date || date;
            const val = Math.round(params[0].value * 10) / 10; // 保留 1 位小数
            return `<b>${fullDate}</b><br/>占用率：${val}%`;
          },
        },
        grid: { left: 56, right: 20, top: 24, bottom: 20, containLabel: true },
        xAxis: {
          type: "category",
          data: xData,
          axisLabel: { fontSize: 11, color: "#374151" },
          axisLine: { lineStyle: { color: "#000000" } },
          boundaryGap: false,
        },
        yAxis: {
          type: "value",
          name: "占用率(%)",
          min: 0,
          // max 不固定，让 echarts 自动适配（数据一般在 0-10% 区间）
          nameTextStyle: { fontSize: 11, color: "#dc2626" },
          axisLabel: {
            fontSize: 11,
            color: "#374151",
            formatter: (v) => Math.round(v) + "%", // Y 轴刻度整数
          },
          splitLine: { lineStyle: { color: "#e5e7eb" } },
          axisLine: { lineStyle: { color: "#dc2626" } },
        },
        series: [
          {
            name: "占用率",
            type: "line",
            data: rates,
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            lineStyle: { width: 2.5, color: "#dc2626" },
            itemStyle: { color: "#dc2626" },
            areaStyle: { color: "rgba(220,38,38,0.10)" },
          },
        ],
      });
      requestAnimationFrame(() => chartRate.resize());
      this.trendChartRate = chartRate;
      // 统一 resize 监听
      this._trendResizeHandler = () => {
        chartArea.resize();
        chartRate.resize();
      };
      window.addEventListener("resize", this._trendResizeHandler);
    },
    formatArea(v) {
      if (v == null || v === 0) return "暂无数据";
      return v >= 10000 ? (v / 10000).toFixed(2) + " 万m²" : v + " m²";
    },
    formatRate(v) {
      if (v == null) return "暂无数据";
      return v + "%";
    },
    getOccupyClass(rate) {
      if (rate == null) return "";
      if (rate >= 90) return "area-danger";
      if (rate >= 70) return "area-warn";
      return "area-ok";
    },
    getOccupyColor(rate) {
      if (rate == null) return "#475569";
      if (rate >= 90) return "#ef4444";
      if (rate >= 70) return "#f59e0b";
      return "#22c55e";
    },
    getRingDash(rate) {
      const r = Math.max(0, Math.min(100, rate || 0));
      const circ = 2 * Math.PI * 50;
      const filled = (r / 100) * circ;
      return filled + " " + (circ - filled);
    },
    formatAreaShort(v) {
      if (v == null || v === 0) return "--";
      return v >= 10000 ? (v / 10000).toFixed(1) + "万m²" : v + "m²";
    },
    getStatusLabel(rate) {
      if (rate == null) return "无数据";
      if (rate < 30) return "空闲";
      if (rate <= 100) return "正常";
      if (rate <= 110) return "超面积";
      return "严重超面积";
    },
    getStatusClass(rate) {
      if (rate == null) return "";
      if (rate < 30) return "tag-free";
      if (rate <= 100) return "tag-normal";
      if (rate <= 110) return "tag-over";
      return "tag-full";
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
    },
    batchPlaceAll() {
      if (!this.drawerFilteredLocations.length) return;
      const toPlace = this.drawerFilteredLocations.slice(0, 20);
      console.log("[batchPlaceAll] sample:", toPlace[0]);
      const gap = 6;
      const startX = 40;
      let startY = 40;
      for (const item of this.inventoryData) {
        const bottom = (Number(item.y) || 0) + (Number(item.h) || 0);
        if ((Number(item.x) || 0) < startX + 200 && bottom >= startY)
          startY = bottom + gap;
      }
      const placed = [];
      const placeIds = new Set();
      let curY = startY;
      for (let i = 0; i < toPlace.length; i++) {
        const u = toPlace[i];
        console.log(
          "[batchPlaceAll] item:",
          u.name,
          "pallet_weight:",
          u.pallet_weight,
          "palletWeight:",
          u.palletWeight,
          "width:",
          u.width,
        );
        const wt = u.wareType ?? u.ware_type ?? 2;
        let pw, ph, w, h;
        if (wt === 2) {
          pw = Math.max(0.5, Number(u.pallet_weight || u.palletWeight) || 1);
          ph = Math.max(0.5, Number(u.pallet_height || u.palletHeight) || 1);
          w = pw * this.baseCellW;
          h = ph * this.baseCellH;
        } else {
          const apiW = Number(u.width) || 0;
          const apiH = Number(u.height) || 0;
          pw = apiW ? Math.round(apiW / this.baseCellW) || 1 : 1;
          ph = apiH ? Math.round(apiH / this.baseCellH) || 1 : 1;
          w = apiW || this.baseCellW;
          h = apiH || this.baseCellH;
        }
        placed.push({
          id: u.id,
          name: u.name || u.locationNo || "",
          fullName: u.fullName || u.locationName || "",
          materialNo: u.materialNo,
          quantity: u.quantity,
          x: startX,
          y: curY,
          z: u.z ?? 1,
          w,
          h,
          palletWeight: pw,
          palletHeight: ph,
          wareType: wt,
        });
        curY += h + gap;
        placeIds.add(u.id);
      }
      this.inventoryData.push(...placed);
      this._clearPassageBorderCache();
      this.unplacedLocations = this.unplacedLocations.filter(
        (u) => !placeIds.has(u.id),
      );
      this.drawerSearchKey = "";
      this.updateCanvasSize();
      this.startRenderChunks();
      this.myToast(`已摆放 ${placed.length} 个库位`, "success");
    },
    findEmptySpot(x, y, w, h) {
      if (!this.checkOverlap(null, w, h, x, y)) return { x, y };
      for (let r = 0; r < 200; r++) {
        for (let c = 0; c < 40; c++) {
          const nx = 40 + c * (w + 6);
          const ny = 40 + r * (h + 6);
          if (!this.checkOverlap(null, w, h, nx, ny)) return { x: nx, y: ny };
        }
      }
      return null;
    },
    handlePlacedContextMenu(e, item) {
      if (!this.editMode) return;
      if (!item || !item.id) return;

      const idx = this.inventoryData.findIndex((x) => x.id === item.id);
      if (idx < 0) return;

      this.inventoryData.splice(idx, 1);
      this.unplacedLocations.unshift({
        id: item.id,
        name: item.name || "",
        fullName: item.fullName || "",
        materialNo: item.materialNo,
        quantity: item.quantity,
        z: null,
        x: null,
        y: null,
        w: null,
        h: null,
        width: null,
        height: null,
        pallet_weight: item.palletWeight || item.pallet_weight || null,
        pallet_height: item.palletHeight || item.pallet_height || null,
        wareType: item.wareType || 2,
      });
      this.updateCanvasSize();
    },
    handleCanvasDragEnter(e) {
      if (!this.editMode) return;
      try {
        e.preventDefault();
      } catch (_) {
        void 0;
      }
    },
    handleCanvasDragOver(e) {
      if (!this.editMode) return;
      try {
        e.preventDefault();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = "move";
        }
      } catch (_) {
        void 0;
      }
    },
    handleCanvasDrop(e) {
      if (!this.editMode) return;

      try {
        e.preventDefault();
      } catch (_) {
        void 0;
      }

      const viewport = this.$refs.warehouseViewport;
      if (!viewport) return;

      let id = "";
      let draggedData = null;

      try {
        id = e.dataTransfer.getData("text/plain");
        if (!id) id = e.dataTransfer.getData("text");
        if (!id) id = e.dataTransfer.getData("Text");
        const jsonData = e.dataTransfer.getData("application/json");
        if (jsonData) {
          try {
            draggedData = JSON.parse(jsonData);
          } catch (parseError) {
            console.warn("[Dashboard] 解析拖拽数据失败:", parseError);
          }
        }
      } catch (error) {
        console.warn("[Dashboard] 获取拖拽数据失败:", error);
      }

      id = id || this.draggingUnplacedId;
      if (!id && this.draggedUnplacedItem) {
        id = this.draggedUnplacedItem.id;
        draggedData = this.draggedUnplacedItem;
      }

      if (!id) {
        console.warn("[Dashboard] 无法获取拖拽项目ID");
        return;
      }

      const idx = this.unplacedLocations.findIndex((x) => x.id === id);
      if (idx < 0) {
        console.warn("[Dashboard] 未找到对应的未摆放库位:", id);
        return;
      }

      const u = draggedData || this.unplacedLocations[idx];
      console.log(
        "[Dashboard] drop item:",
        u,
        "pallet_weight:",
        u.pallet_weight,
        "palletWeight:",
        u.palletWeight,
      );

      const rect = viewport.getBoundingClientRect();
      const padLeft = 12;
      const padTop = 44;
      const xInViewport = e.clientX - rect.left + viewport.scrollLeft - padLeft;
      const yInViewport = e.clientY - rect.top + viewport.scrollTop - padTop;

      const x = xInViewport / this.effectiveScale;
      const y = yInViewport / this.effectiveScale;

      const wt = u.wareType ?? u.ware_type ?? 2;
      let pw, ph, w, h;
      if (wt === 2) {
        pw = Math.max(0.5, Number(u.pallet_weight || u.palletWeight) || 1);
        ph = Math.max(0.5, Number(u.pallet_height || u.palletHeight) || 1);
        w = pw * this.baseCellW;
        h = ph * this.baseCellH;
      } else {
        const apiW = Number(u.width) || 0;
        const apiH = Number(u.height) || 0;
        pw = apiW ? Math.round(apiW / this.baseCellW) || 1 : 1;
        ph = apiH ? Math.round(apiH / this.baseCellH) || 1 : 1;
        w = apiW || this.baseCellW;
        h = apiH || this.baseCellH;
      }

      const nx = Math.max(
        this.warehouse.minX,
        Math.min(this.warehouse.maxX - w, x),
      );
      const ny = Math.max(
        this.warehouse.minY,
        Math.min(this.warehouse.maxY - h, y),
      );

      if (this.checkOverlap(null, w, h, nx, ny, u.id)) {
        this.draggingUnplacedId = "";
        return;
      }

      this.inventoryData.push({
        id: u.id,
        name: u.name,
        x: nx,
        y: ny,
        z: 1,
        w,
        h,
        palletWeight: pw,
        palletHeight: ph,
        wareType: wt,
      });
      this._clearPassageBorderCache();
      this.unplacedLocations.splice(idx, 1);
      this.draggingUnplacedId = "";
      this.updateCanvasSize();
      this.startRenderChunks();
    },
    closeSearchPanel() {
      if (this.highlightRunning) {
        this.showSearchPanel = false;
        return;
      }
      this.showSearchPanel = false;
      this.searchKeyword = "";
      this.searchMaterialKeyword = "";
      this.searchSuppliers = [];
      this.searchMaterialResults = [];
      this.searchSelectedSupplier = "";
      this.highlightProgress = "";
    },
    clearHighlights() {
      this.highlightedLocations = new Set();
      this.selectedLocationKey = "";
    },
    searchMaterialInSupplier() {},
    searchBack() {
      if (this.highlightProgress) return;
      if (this.searchState === "material") {
        this.searchState = "supplier";
        this.searchMaterials = [];
      } else if (this.searchState === "supplier") {
        this.searchState = "idle";
        this.searchSuppliers = [];
        this.searchKeyword = "";
      }
      this.highlightedLocations = new Set();
      this.selectedLocationKey = "";
    },
    searchSupplier() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.doSearchSupplier(), 500);
    },
    async doSearchSupplier() {
      const kw = String(this.searchKeyword || "").trim();
      if (!kw || kw.length < 1) {
        this.searchSuppliers = [];
        return;
      }
      this.searchLoading = true;
      this.searchSuppliers = [];
      try {
        const res = await request.get("/uic-system/supplier/page", {
          params: { supplierName: kw, current: 1, size: 20 },
        });
        const payload = res?.data || {};
        const list = payload?.records || payload?.list || payload?.data || [];
        this.searchSuppliers = Array.isArray(list)
          ? list.map((s) => ({
              supplierName: s.supplierName || s.name || "",
              supplierId: s.id || s.supplierId,
            }))
          : [];
      } catch (e) {
        this.searchSuppliers = [];
      } finally {
        this.searchLoading = false;
      }
    },
    searchMaterial() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.doSearchMaterial(), 500);
    },
    async doSearchMaterial() {
      const kw = String(this.searchMaterialKeyword || "").trim();
      if (!kw || kw.length < 2) {
        this.searchMaterialResults = [];
        return;
      }
      this.searchLoading = true;
      this.searchMaterialResults = [];
      try {
        const params = { materialNo: kw, current: 1, size: 500 };
        if (this.searchSelectedSupplier) {
          const sup = this.searchSuppliers.find(
            (s) => s.supplierName === this.searchSelectedSupplier,
          );
          if (sup) params.supplierId = sup.supplierId || sup.id;
        }
        const res = await request.get("/uic-system/material/page", {
          params,
          timeout: 15000,
        });
        const payload = res?.data || {};
        const list = payload?.records || payload?.list || payload?.data || [];
        this.searchMaterialResults = Array.isArray(list)
          ? list.map((m) => ({
              materialNo: m.materialNo || m.materialCode || "",
              materialId: m.id || m.materialId || "",
              materialDesc: m.materialDesc || "",
              stockNum: m.stockNum ?? m.stock ?? 0,
              supplierName: m.supplierName || "",
            }))
          : [];
      } catch (e) {
        this.searchMaterialResults = [];
      } finally {
        this.searchLoading = false;
      }
    },
    clearSupplierFilter() {
      this.searchSelectedSupplier = "";
      this.searchMaterialResults = [];
      this.searchMaterialKeyword = "";
    },
    async highlightAllMaterials() {
      const mats = this.searchMaterialResults;
      if (!mats.length || this.highlightRunning) return;
      this.showHeatmap = false;
      const confirmed = await this.myConfirm(
        "确认点亮",
        `确认要逐个点亮所有 ${mats.length} 个物料的库位吗？`,
      );
      if (!confirmed) return;
      this.highlightRunning = true;
      this.highlightCancelled = false;
      this.highlightedLocations = new Set();
      const total = mats.length;
      for (let i = 0; i < total; i++) {
        if (this.highlightCancelled) break;
        this.highlightProgress = `点亮中 ${i + 1}/${total}`;
        const m = mats[i];
        try {
          const params = {
            stockId: this.selectedWareId,
            warehouseId: this.selectedWareId,
          };
          if (m.materialId) params.materialId = m.materialId;
          else params.materialNo = m.materialNo;
          const res = await request.get(
            "/api_hf/uic-system/ware/location/detail",
            { params },
          );
          const payload = res?.data || [];
          const rows = Array.isArray(payload)
            ? payload
            : payload
            ? [payload]
            : [];
          if (rows.length > 0) {
            const locName = rows[0].locationNo || rows[0].locationName || "";
            if (locName && !this.highlightedLocations.has(locName)) {
              this.highlightedLocations.add(locName);
            }
          }
        } catch (_) {
          void 0;
        }
        await new Promise((r) => setTimeout(r, 20));
      }
      const count = this.highlightedLocations.size;
      this.highlightProgress = "";
      this.highlightRunning = false;
      if (count) {
        const el = this.$refs.warehouseViewport;
        if (el) {
          const matched = this.inventoryData.filter((x) =>
            this.highlightedLocations.has(x.name),
          );
          if (matched.length) {
            const s = this.effectiveScale;
            const cx =
              matched.reduce((a, x) => a + x.x + x.w / 2, 0) / matched.length;
            const cy =
              matched.reduce((a, x) => a + x.y + x.h / 2, 0) / matched.length;
            el.scrollLeft = cx * s - el.clientWidth / 2;
            el.scrollTop = cy * s - el.clientHeight / 2;
          }
        }
        this.$nextTick(() => this.drawAllPallets());
        this.myToast(`已高亮 ${count} 个库位`, "success");
      } else {
        this.myToast("所搜物料的库位未在画布上摆放", "error");
      }
    },
    async selectSupplier(supplier) {
      this.searchSelectedSupplier = supplier.supplierName;
      this.highlightedLocations = new Set();
      this.searchLoading = true;
      this.searchMaterialResults = [];
      this.searchMaterials = [];
      if (this.isMobile) {
        this.searchState = "material";
      }
      try {
        const res = await request.get("/uic-system/material/page", {
          params: {
            supplierId: supplier.supplierId || supplier.id,
            current: 1,
            size: 500,
            type: 1,
            isCompose: 0,
          },
          timeout: 15000,
        });
        const payload = res?.data || {};
        const list = payload?.records || payload?.list || payload?.data || [];
        const materials = Array.isArray(list)
          ? list.map((m) => ({
              materialNo: m.materialNo || m.materialCode || "",
              materialId: m.id || m.materialId || "",
              materialDesc: m.materialDesc || "",
              stockNum: m.stockNum ?? m.stock ?? 0,
              supplierName: supplier.supplierName,
            }))
          : [];
        this.searchMaterialResults = materials;
        this.searchMaterials = materials;
      } catch (e) {
        this.searchMaterialResults = [];
        this.searchMaterials = [];
      } finally {
        this.searchLoading = false;
      }
    },
    async selectMaterial(m) {
      this.searchLoading = true;
      this.selectedLocationKey = "";
      this.highlightedLocations = new Set();
      try {
        const params = {
          stockId: this.selectedWareId,
          warehouseId: this.selectedWareId,
        };
        if (m.materialId) params.materialId = m.materialId;
        else params.materialNo = m.materialNo;
        const res = await request.get(
          "/api_hf/uic-system/ware/location/detail",
          { params },
        );
        const payload = res?.data || [];
        const rows = Array.isArray(payload)
          ? payload
          : payload
          ? [payload]
          : [];
        if (rows.length > 0) {
          const row = rows[0];
          const locName = row.locationNo || row.locationName || "";
          const locId = row.locationId;
          const placed = this.inventoryData.find(
            (x) =>
              (locId && (x.id === locId || String(x.id) === String(locId))) ||
              (locName && x.name === locName),
          );
          if (placed) {
            this.selectedLocationKey = placed.name;
            const el = this.$refs.warehouseViewport;
            if (el) {
              const s = this.effectiveScale;
              el.scrollLeft = placed.x * s - el.clientWidth / 2;
              el.scrollTop = placed.y * s - el.clientHeight / 2;
            }
          } else {
            this.myToast(
              `物料 ${m.materialNo} 所在库位(${locName})未在画布上摆放`,
              "error",
            );
          }
        } else {
          this.myToast(`未找到物料 ${m.materialNo} 的库位信息`, "error");
        }
      } catch (e) {
        this.myToast("查询库位失败", "error");
      } finally {
        this.searchLoading = false;
      }
    },
    highlightLocation(loc) {
      const placed = this.inventoryData.find(
        (x) => x.id === loc.id || x.id === loc.locationId,
      );
      if (placed) {
        this.selectedLocationKey = placed.name;
        this.showSearchPanel = false;
        const el = this.$refs.warehouseViewport;
        if (el) {
          const s = this.effectiveScale;
          el.scrollLeft = placed.x * s - el.clientWidth / 2;
          el.scrollTop = placed.y * s - el.clientHeight / 2;
        }
      }
    },
    async highlightSupplier() {
      const mats = this.searchMaterialResults;
      if (!mats.length) return;
      if (this.highlightProgress) return;
      this.showHeatmap = false;
      this.highlightedLocations = new Set();
      const total = mats.length;
      for (let i = 0; i < total; i++) {
        this.highlightProgress = `查询中 ${i + 1}/${total}`;
        const m = mats[i];
        try {
          const params = {
            stockId: this.selectedWareId,
            warehouseId: this.selectedWareId,
          };
          if (m.materialId) params.materialId = m.materialId;
          else params.materialNo = m.materialNo;
          const res = await request.get(
            "/api_hf/uic-system/ware/location/detail",
            { params },
          );
          const payload = res?.data || [];
          const rows = Array.isArray(payload)
            ? payload
            : payload
            ? [payload]
            : [];
          if (rows.length > 0) {
            const locName = rows[0].locationNo || rows[0].locationName || "";
            if (locName && !this.highlightedLocations.has(locName)) {
              this.highlightedLocations.add(locName);
              await new Promise((r) => setTimeout(r, 20));
            }
          }
        } catch (_) {
          void 0;
        }
      }
      const count = this.highlightedLocations.size;
      this.highlightProgress = "";
      if (count) {
        const el = this.$refs.warehouseViewport;
        if (el) {
          const matched = this.inventoryData.filter((x) =>
            this.highlightedLocations.has(x.name),
          );
          if (matched.length) {
            const s = this.effectiveScale;
            const cx =
              matched.reduce((a, x) => a + x.x + x.w / 2, 0) / matched.length;
            const cy =
              matched.reduce((a, x) => a + x.y + x.h / 2, 0) / matched.length;
            el.scrollLeft = cx * s - el.clientWidth / 2;
            el.scrollTop = cy * s - el.clientHeight / 2;
          }
        }
        this.$nextTick(() => this.drawAllPallets());
        this.myToast(`已高亮 ${count} 个库位`, "success");
      } else {
        this.myToast("该供应商的库位未在画布上摆放", "error");
      }
    },
    selectSearchResult(row) {
      const loc = row?.location;
      if (!loc) return;

      const placed = this.inventoryData.find(
        (x) =>
          (x?.id && loc?.id && x.id === loc.id) ||
          (x?.id && loc?.locationId && x.id === loc.locationId),
      );
      this.selectedLocationKey = placed?.name || loc.name || "";
      this.selectedMaterialNo = row?.material?.materialNo || "";
      this.selectedSupplierName = row?.material?.supplierName || "";
    },
    getLocationFontSize(item) {
      const w = Number(item?.w) || 0;
      const h = Number(item?.h) || 0;
      const name = item?.name || "";
      const minDim = Math.min(w, h);
      let px = Math.max(6, Math.min(14, Math.floor(minDim / 4)));
      if (name.length > 4 && minDim < 100) {
        px = Math.max(6, Math.floor((minDim / name.length) * 1.2));
      }
      if (
        item?.wareType === 11 ||
        item?.wareType === 10 ||
        item?.wareType === 8 ||
        item?.wareType === 6 ||
        item?.wareType === 9
      ) {
        px = Math.floor(minDim * 0.175);
      }
      return `${px}px`;
    },
    getWallCoverStyle(item) {
      const x = Number(item.x) || 0;
      const y = Number(item.y) || 0;
      const w = Number(item.w) || 0;
      const h = Number(item.h) || 0;
      if (!w || !h) return {};
      const bw = 2;
      const pad = 1;
      const tol = 3;
      const strips = [];
      for (const other of this.inventoryData) {
        if (other.id === item.id || other.wareType !== 4) continue;
        const ox = Number(other.x) || 0;
        const oy = Number(other.y) || 0;
        const ow = Number(other.w) || 0;
        const oh = Number(other.h) || 0;
        if (Math.abs(y + h - oy) < tol && ox < x + w && ox + ow > x) {
          const l = Math.max(x, ox) - x + bw;
          const len = Math.min(x + w, ox + ow) - Math.max(x, ox) - bw * 2;
          if (len > 0)
            strips.push({ l, t: h - bw - pad, w: len, h: bw + pad * 2 });
        }
        if (Math.abs(y - (oy + oh)) < tol && ox < x + w && ox + ow > x) {
          const l = Math.max(x, ox) - x + bw;
          const len = Math.min(x + w, ox + ow) - Math.max(x, ox) - bw * 2;
          if (len > 0) strips.push({ l, t: -pad, w: len, h: bw + pad * 2 });
        }
        if (Math.abs(x + w - ox) < tol && oy < y + h && oy + oh > y) {
          const t = Math.max(y, oy) - y + bw;
          const len = Math.min(y + h, oy + oh) - Math.max(y, oy) - bw * 2;
          if (len > 0)
            strips.push({ l: w - bw - pad, t, w: bw + pad * 2, h: len });
        }
        if (Math.abs(x - (ox + ow)) < tol && oy < y + h && oy + oh > y) {
          const t = Math.max(y, oy) - y + bw;
          const len = Math.min(y + h, oy + oh) - Math.max(y, oy) - bw * 2;
          if (len > 0) strips.push({ l: -pad, t, w: bw + pad * 2, h: len });
        }
      }
      if (!strips.length) return { display: "none" };
      const bgs = [];
      const positions = [];
      const sizes = [];
      for (const s of strips) {
        bgs.push("linear-gradient(#0A0D15,#0A0D15)");
        positions.push(`${s.l}px ${s.t}px`);
        sizes.push(`${s.w}px ${s.h}px`);
      }
      return {
        backgroundImage: bgs.join(", "),
        backgroundPosition: positions.join(", "),
        backgroundSize: sizes.join(", "),
        backgroundRepeat: "no-repeat",
      };
    },
    getPassageCoverStyle(item) {
      const x = Number(item.x) || 0;
      const y = Number(item.y) || 0;
      const w = Number(item.w) || 0;
      const h = Number(item.h) || 0;
      if (!w || !h) return {};
      const bw = 4;
      const pad = 1;
      const tol = 3;
      const strips = [];
      for (const other of this.inventoryData) {
        if (other.id === item.id || other.wareType !== 5) continue;
        const ox = Number(other.x) || 0;
        const oy = Number(other.y) || 0;
        const ow = Number(other.w) || 0;
        const oh = Number(other.h) || 0;
        // bottom: widen both sides, keep left/right border gap
        if (Math.abs(y + h - oy) < tol && ox < x + w && ox + ow > x) {
          const l = Math.max(x, ox) - x + bw;
          const len = Math.min(x + w, ox + ow) - Math.max(x, ox) - bw * 2;
          if (len > 0)
            strips.push({ l, t: h - bw - pad, w: len, h: bw + pad * 2 });
        }
        // top: widen both sides, keep left/right border gap
        if (Math.abs(y - (oy + oh)) < tol && ox < x + w && ox + ow > x) {
          const l = Math.max(x, ox) - x + bw;
          const len = Math.min(x + w, ox + ow) - Math.max(x, ox) - bw * 2;
          if (len > 0) strips.push({ l, t: -pad, w: len, h: bw + pad * 2 });
        }
        // right: widen both sides, keep top/bottom border gap
        if (Math.abs(x + w - ox) < tol && oy < y + h && oy + oh > y) {
          const t = Math.max(y, oy) - y + bw;
          const len = Math.min(y + h, oy + oh) - Math.max(y, oy) - bw * 2;
          if (len > 0)
            strips.push({ l: w - bw - pad, t, w: bw + pad * 2, h: len });
        }
        // left: widen both sides, keep top/bottom border gap
        if (Math.abs(x - (ox + ow)) < tol && oy < y + h && oy + oh > y) {
          const t = Math.max(y, oy) - y + bw;
          const len = Math.min(y + h, oy + oh) - Math.max(y, oy) - bw * 2;
          if (len > 0) strips.push({ l: -pad, t, w: bw + pad * 2, h: len });
        }
      }
      if (!strips.length) return { display: "none" };
      const bgs = [];
      const positions = [];
      const sizes = [];
      for (const s of strips) {
        // 为每段相邻通道的衔接条构建绿色渐变背景、偏移位置与尺寸
        // 这些数据后续会被拼接为 CSS background 样式，用于在通道之间绘制绿色连接带
        bgs.push("linear-gradient(#00833E,#00833E)");
        positions.push(`${s.l}px ${s.t}px`);
        sizes.push(`${s.w}px ${s.h}px`);
      }
      return {
        backgroundImage: bgs.join(", "),
        backgroundPosition: positions.join(", "),
        backgroundSize: sizes.join(", "),
        backgroundRepeat: "no-repeat",
      };
    },
    getGateStyle(item) {
      if (item.wareType !== 6) return {};
      const w = Number(item.w) || 0;
      const h = Number(item.h) || 0;
      if (!w || !h) return {};
      const isHorizontal = w >= h;
      const boxSize = isHorizontal
        ? Math.min(h * 0.4, 20)
        : Math.min(w * 0.4, 20);
      return {
        "--gate-dir": isHorizontal ? "row" : "column",
        "--gate-box": boxSize + "px",
      };
    },
    getPassageBorders(item) {
      if (item.wareType !== 5) return {};
      const x = Number(item.x) || 0;
      const y = Number(item.y) || 0;
      const w = Number(item.w) || 0;
      const h = Number(item.h) || 0;
      if (!w || !h) return {};
      if (!this._passageBorderCache) this._passageBorderCache = new Map();
      const cacheKey = `${item.id}_${x}_${y}_${w}_${h}`;
      if (this._passageBorderCache.has(cacheKey)) {
        return this._passageBorderCache.get(cacheKey);
      }
      const bw = 4;
      const tol = 3;
      const strips = [];
      for (const other of this.inventoryData) {
        if (other.id === item.id || other.wareType !== 5) continue;
        const ox = Number(other.x) || 0;
        const oy = Number(other.y) || 0;
        const ow = Number(other.w) || 0;
        const oh = Number(other.h) || 0;
        if (Math.abs(y + h - oy) < tol && ox < x + w && ox + ow > x) {
          const s = Math.max(x, ox) - x;
          const e = Math.min(x + w, ox + ow) - x;
          if (e > s) strips.push({ side: "bottom", from: s, len: e - s });
        }
        if (Math.abs(y - (oy + oh)) < tol && ox < x + w && ox + ow > x) {
          const s = Math.max(x, ox) - x;
          const e = Math.min(x + w, ox + ow) - x;
          if (e > s) strips.push({ side: "top", from: s, len: e - s });
        }
        if (Math.abs(x + w - ox) < tol && oy < y + h && oy + oh > y) {
          const s = Math.max(y, oy) - y;
          const e = Math.min(y + h, oy + oh) - y;
          if (e > s) strips.push({ side: "right", from: s, len: e - s });
        }
        if (Math.abs(x - (ox + ow)) < tol && oy < y + h && oy + oh > y) {
          const s = Math.max(y, oy) - y;
          const e = Math.min(y + h, oy + oh) - y;
          if (e > s) strips.push({ side: "left", from: s, len: e - s });
        }
      }
      if (!strips.length) {
        this._passageBorderCache.set(cacheKey, {});
        return {};
      }
      const bgs = [];
      const positions = [];
      const sizes = [];
      for (const s of strips) {
        bgs.push(
          "linear-gradient(rgba(209,213,219,0.08),rgba(209,213,219,0.08))",
        );
        if (s.side === "bottom") {
          positions.push(`${s.from + bw}px calc(100% - ${bw}px)`);
          sizes.push(`${s.len}px ${bw}px`);
        } else if (s.side === "top") {
          positions.push(`${s.from + bw}px ${bw}px`);
          sizes.push(`${s.len}px ${bw}px`);
        } else if (s.side === "right") {
          positions.push(`calc(100% - ${bw}px) ${s.from + bw}px`);
          sizes.push(`${bw}px ${s.len}px`);
        } else if (s.side === "left") {
          positions.push(`${bw}px ${s.from + bw}px`);
          sizes.push(`${bw}px ${s.len}px`);
        }
      }
      const result = {
        "--p-bg": bgs.join(","),
        "--p-pos": positions.join(","),
        "--p-size": sizes.join(","),
      };
      this._passageBorderCache.set(cacheKey, result);
      return result;
    },
    _clearPassageBorderCache() {
      if (this._passageBorderCache) {
        this._passageBorderCache.clear();
      }
    },
    getHeatColor(occupancy) {
      const c = this.getHeatColorRGB(occupancy);
      return `rgb(${c.r},${c.g},${c.b})`;
    },
    getHeatColorRGB(occupancy) {
      // 12 档离散色阶（0/10/20/30/40/50/60/70/80/90/100/110+）
      const occ = Math.max(0, occupancy ?? 0);
      const pct = Math.min(occ * 100, 110);
      const idx = Math.min(11, Math.floor(pct / 10));
      const steps = [
        { r: 0, g: 176, b: 80 }, // 0%   #00B050 绿
        { r: 46, g: 178, b: 66 }, // 10%  #2EB242 绿
        { r: 92, g: 181, b: 51 }, // 20%  #5CB533 黄绿
        { r: 139, g: 184, b: 37 }, // 30%  #8BB825 黄绿
        { r: 185, g: 187, b: 22 }, // 40%  #B9BB16 黄
        { r: 231, g: 190, b: 8 }, // 50%  #E7BE08 黄
        { r: 255, g: 175, b: 0 }, // 60%  #FFAF00 橙黄
        { r: 255, g: 140, b: 0 }, // 70%  #FF8C00 橙
        { r: 255, g: 105, b: 0 }, // 80%  #FF6900 深橙
        { r: 255, g: 70, b: 0 }, // 90%  #FF4600 橙红
        { r: 255, g: 35, b: 0 }, // 100% #FF2300 红
        { r: 255, g: 0, b: 0 }, // 100+ #FF0000 纯红
      ];
      return steps[idx];
    },
    getPalletStyle(item) {
      const pw = Math.max(0.5, item.palletWeight || 1);
      const ph = Math.max(0.5, item.palletHeight || 1);
      const total = pw * ph;
      const occ = Math.max(0, item.occupancy || 0);
      const litCount = Math.min(total, Math.floor(occ * total));
      const c = this.getHeatColorRGB(occ);
      const lit = `rgba(${c.r},${c.g},${c.b},0.65)`;
      const dim = `rgba(255,255,255,0.04)`;
      const gap = 3;
      const parts = [];

      for (let row = 0; row < ph; row++) {
        for (let col = 0; col < pw; col++) {
          const idx = row * pw + col;
          const fill = idx < litCount ? lit : dim;
          const x = (col / pw) * 100;
          const y = (row / ph) * 100;
          const w = 100 / pw;
          const h = 100 / ph;
          parts.push(
            `linear-gradient(${fill},${fill}) calc(${x}% + ${gap}px) calc(${y}% + ${gap}px) / calc(${w}% - ${
              gap * 2
            }px) calc(${h}% - ${gap * 2}px) no-repeat`,
          );
        }
      }

      return { background: parts.join(",") };
    },
    getItemMaterials(item) {
      if (!item) return [];
      if (Array.isArray(item.materials) && item.materials.length > 0) {
        return item.materials.map((m) => ({
          materialNo: m.materialNo || m.materialCode || m.code || "",
          quantity: m.quantity ?? m.qty ?? m.num ?? m.stock ?? "",
        }));
      }
      if (item.materialNo !== undefined || item.quantity !== undefined) {
        return [
          { materialNo: item.materialNo || "", quantity: item.quantity ?? "" },
        ];
      }
      return [];
    },
    startResize(e, item, direction = "se") {
      if (!this.editMode) return;
      try {
        e.preventDefault();
      } catch (_) {
        void 0;
      }
      const scale = Number(this.effectiveScale) || 1;
      const startX = e.clientX;
      const startY = e.clientY;
      const originW = item.w;
      const originH = item.h;
      const originX = item.x;
      const originY = item.y;
      const minSize = this.gridSize;
      let raf = 0;
      let pending = null;
      const move = (ev) => {
        pending = { x: ev.clientX, y: ev.clientY };
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          if (!pending) return;
          const dx = (pending.x - startX) / scale;
          const dy = (pending.y - startY) / scale;
          pending = null;
          // 根据方向计算新的尺寸和位置
          if (direction.includes("e")) {
            item.w = Math.max(
              minSize,
              Math.round((originW + dx) / this.gridSize) * this.gridSize,
            );
          }
          if (direction.includes("s")) {
            item.h = Math.max(
              minSize,
              Math.round((originH + dy) / this.gridSize) * this.gridSize,
            );
          }
          if (direction.includes("w")) {
            const newW = Math.max(
              minSize,
              Math.round((originW - dx) / this.gridSize) * this.gridSize,
            );
            item.x = originX + originW - newW;
            item.w = newW;
          }
          if (direction.includes("n")) {
            const newH = Math.max(
              minSize,
              Math.round((originH - dy) / this.gridSize) * this.gridSize,
            );
            item.y = originY + originH - newH;
            item.h = newH;
          }
        });
      };
      const up = () => {
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", up);
        document.removeEventListener("pointercancel", up);
        this.updateCanvasSize();
        if (raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
      };
      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", up);
      document.addEventListener("pointercancel", up);
    },
    startDrag(e, item) {
      if (!this.editMode) return;
      e.preventDefault();
      e.stopPropagation();

      const isBatchDrag =
        this.selectedBatchItems.length > 1 &&
        this.selectedBatchItems.includes(item);
      const dragItems = isBatchDrag ? this.selectedBatchItems : [item];
      const origins = dragItems.map((i) => ({
        item: i,
        x: Number(i.x) || 0,
        y: Number(i.y) || 0,
        originX: Number(i.x) || 0,
        originY: Number(i.y) || 0,
      }));

      const startClientX = e.clientX;
      const startClientY = e.clientY;
      let moved = false;
      let raf = 0;
      let pending = null;
      const move = (ev) => {
        ev.preventDefault();
        moved = true;
        pending = { x: ev.clientX, y: ev.clientY };
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          if (!pending) return;
          const scale = Number(this.effectiveScale) || 1;
          const dx = (pending.x - startClientX) / scale;
          const dy = (pending.y - startClientY) / scale;
          pending = null;

          for (const o of origins) {
            let nx = o.x + dx;
            let ny = o.y + dy;
            nx = this.snapToGridValue(nx);
            ny = this.snapToGridValue(ny);
            nx = Math.max(
              this.warehouse.minX,
              Math.min(this.warehouse.maxX - o.item.w, nx),
            );
            ny = Math.max(
              this.warehouse.minY,
              Math.min(this.warehouse.maxY - o.item.h, ny),
            );
            o.item.x = nx;
            o.item.y = ny;
          }
        });
      };
      const up = () => {
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", up);
        document.removeEventListener("pointercancel", up);
        if (!moved) {
          if (raf) {
            cancelAnimationFrame(raf);
            raf = 0;
          }
          return;
        }

        const excludeIds = isBatchDrag
          ? new Set(dragItems.map((i) => i.id))
          : null;
        for (const o of origins) {
          let nx = this.snapToGridValue(o.item.x);
          let ny = this.snapToGridValue(o.item.y);
          nx = Math.max(
            this.warehouse.minX,
            Math.min(this.warehouse.maxX - o.item.w, nx),
          );
          ny = Math.max(
            this.warehouse.minY,
            Math.min(this.warehouse.maxY - o.item.h, ny),
          );
          const hasOverlap = this.checkOverlap(
            o.item,
            o.item.w,
            o.item.h,
            nx,
            ny,
            excludeIds,
          );
          if (hasOverlap) {
            o.item.x = o.originX;
            o.item.y = o.originY;
          } else {
            o.item.x = nx;
            o.item.y = ny;
          }
        }

        this.guideLines = { v: [], h: [] };
        this._clearPassageBorderCache();
        this.updateCanvasSize();

        if (raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
      };

      document.addEventListener("pointermove", move, { passive: false });
      document.addEventListener("pointerup", up);
      document.addEventListener("pointercancel", up);
    },
    handleLocationClick(item) {
      if (!this.isMobile) return;
      if (this.editMode) return;
      this.showDetail(item);
    },
    async showDetail(item) {
      if (item?.wareType !== 2) return;
      this.currentItem = item;
      this.showDialog = true;
      await this.fetchLocationDetail(item);
    },
    async fetchLocationDetail(item) {
      if (item?.wareType !== 2) {
        this.currentLocationDetail = null;
        this.detailLoading = false;
        return;
      }
      const locationId = item?.id || item?.locationId;
      const warehouseId = this.selectedWareId;
      const stockId = this.selectedWareId;
      if (!locationId || !warehouseId || !stockId) {
        this.currentLocationDetail = null;
        this.detailError = !stockId
          ? "缺少 stockId"
          : "缺少 locationId/warehouseId";
        this.detailLoading = false;
        return;
      }

      this.detailLoading = true;
      this.detailError = "";
      try {
        const res = await request.get(
          "/api_hf/uic-system/ware/location/detail",
          {
            params: {
              stockId,
              warehouseId,
              locationId,
            },
          },
        );

        const payload = res?.data || null;
        this.currentLocationDetail =
          this.normalizeLocationDetailPayload(payload);

        const groups = this.getSupplierGroups(this.currentLocationDetail);
        const nextCollapsed = {};
        for (const g of groups) {
          const key = String(g?.supplierName || "").trim() || "未知供应商";
          nextCollapsed[key] = true;
        }
        this.supplierCollapsed = nextCollapsed;
      } catch (e) {
        this.currentLocationDetail = null;
        this.detailError = e?.message || "加载失败";
      } finally {
        this.detailLoading = false;
      }
    },
    closeDialog() {
      this.showDialog = false;
      this.selectedMaterialNo = "";
      this.selectedSupplierName = "";
      this.supplierCollapsed = {};
      this.detailError = "";
    },
    resetView() {
      this.inventoryData = JSON.parse(JSON.stringify(this.originalData));
      this.startRenderChunks();
      this.selectedLocationKey = "";
      this.userScale = 1;
      this.updateCanvasSize();
      this.updateFitScale();
      this.$nextTick(() => setTimeout(() => this.fitToContent(), 100));
    },
    zoomIn() {
      this.showScaling();
      this.userScale = Math.min(this.userScale + 0.1, this.maxUserScale);
    },
    zoomOut() {
      this.showScaling();
      this.userScale = Math.max(this.userScale - 0.1, this.minUserScale);
    },
    handleWheel(e) {
      e.preventDefault();
      this.showScaling();
      const el = this.$refs.warehouseViewport;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const contentX = (el.scrollLeft + mouseX) / this.effectiveScale;
      const contentY = (el.scrollTop + mouseY) / this.effectiveScale;
      const factor = e.deltaY > 0 ? 0.95 : 1.05;
      const newScale = Math.max(
        this.minUserScale,
        Math.min(this.maxUserScale, this.userScale * factor),
      );
      if (newScale === this.userScale) return;
      this.userScale = newScale;
      this.$nextTick(() => {
        el.scrollLeft = contentX * this.effectiveScale - mouseX;
        el.scrollTop = contentY * this.effectiveScale - mouseY;
      });
    },
    showScaling() {
      if (!this.showHeatmap && !this.highlightedLocations.size) return;
      this.scalingInProgress = true;
      if (this._scaleTimer) clearTimeout(this._scaleTimer);
      this._scaleTimer = setTimeout(() => {
        this.scalingInProgress = false;
        this.$nextTick(() => this.drawAllPallets());
      }, 300);
    },
    computePalletStyles() {
      const cache = {};
      for (const item of this.inventoryData) {
        cache[item.id] = this.getPalletStyle(item);
      }
      this.palletStyleCache = cache;
      this.$nextTick(() => this.drawAllPallets());
    },
    setCanvasRef(el, id) {
      if (!this._canvasRefs) this._canvasRefs = {};
      if (el) this._canvasRefs[id] = el;
    },
    drawAllPallets() {
      const hasHighlight = this.highlightedLocations.size > 0;
      if (!this.showHeatmap && !hasHighlight) return;
      for (const item of this.inventoryData) {
        if (item.wareType !== 2) continue;
        // 热力图模式：全量重绘；仅点亮模式：只重绘点亮的库位
        if (
          !this.showHeatmap &&
          hasHighlight &&
          !this.highlightedLocations.has(item.name)
        )
          continue;
        const canvas = this._canvasRefs?.[item.id];
        if (canvas) this.drawPallet(canvas, item);
      }
    },
    drawPallet(canvas, item) {
      const ctx = canvas.getContext("2d");
      const w = canvas.width;
      const h = canvas.height;
      const pw = Math.max(0.5, item.palletWeight || 1);
      const ph = Math.max(0.5, item.palletHeight || 1);
      const total = pw * ph;
      const occ = Math.max(0, item.occupancy || 0);
      const litCount = Math.min(total, Math.floor(occ * total));
      const partialRatio = occ * total - litCount;
      const c = this.getHeatColorRGB(occ);
      const gap = 2;
      const cellW = (w - gap * (pw + 1)) / pw;
      const cellH = (h - gap * (ph + 1)) / ph;

      ctx.clearRect(0, 0, w, h);

      for (let row = 0; row < ph; row++) {
        for (let col = 0; col < pw; col++) {
          const idx = row * pw + col;
          const x = gap + col * (cellW + gap);
          const y = gap + row * (cellH + gap);

          if (idx < litCount) {
            ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},0.7)`;
            ctx.fillRect(x, y, cellW, cellH);
            ctx.strokeStyle = `rgba(${c.r},${c.g},${c.b},0.9)`;
          } else if (idx === litCount && partialRatio > 0) {
            ctx.fillStyle = `rgba(255,255,255,0.04)`;
            ctx.fillRect(x, y, cellW, cellH);
            ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},0.7)`;
            ctx.fillRect(x, y, cellW * partialRatio, cellH);
            ctx.strokeStyle = `rgba(${c.r},${c.g},${c.b},0.5)`;
          } else {
            ctx.fillStyle = `rgba(255,255,255,0.04)`;
            ctx.fillRect(x, y, cellW, cellH);
            ctx.strokeStyle = `rgba(255,255,255,0.15)`;
          }

          ctx.lineWidth = 1;
          ctx.strokeRect(x, y, cellW, cellH);
        }
      }
    },
    rotateLocation(item) {
      const tmp = item.palletWeight;
      item.palletWeight = item.palletHeight;
      item.palletHeight = tmp;
      item.w = item.palletWeight * this.baseCellW;
      item.h = item.palletHeight * this.baseCellH;
      if (this.showHeatmap) {
        this.palletStyleCache[item.id] = this.getPalletStyle(item);
      }
      this.updateCanvasSize();
    },
    async saveToBackend() {
      if (!this.editMode) return;

      // 过滤前端临时ID（如复制功能生成的 copy_ 前缀字符串ID）
      const isTempId = (id) => typeof id === "string" && id.startsWith("copy_");

      const modifiedLocations = [];
      this.inventoryData.forEach((current) => {
        const original = this.originalData.find(
          (orig) => orig.id === current.id,
        );
        if (
          !original ||
          original.x !== current.x ||
          original.y !== current.y ||
          original.w !== current.w ||
          original.h !== current.h
        ) {
          if (isTempId(current.id)) return; // 跳过临时复制的库位
          modifiedLocations.push({
            id: current.id,
            locationNo: current.name || current.locationNo,
            locationName: current.fullName || current.locationName,
            x: current.x,
            y: current.y,
            z: current.z || 1,
            width: current.w,
            height: current.h,
            palletWeight: current.palletWeight,
            palletHeight: current.palletHeight,
          });
        }
      });

      const currentPlacedIdSet = new Set(this.inventoryData.map((x) => x.id));
      this.originalData.forEach((orig) => {
        if (!orig?.id) return;
        if (currentPlacedIdSet.has(orig.id)) return;
        if (modifiedLocations.some((m) => m.id === orig.id)) return;
        if (isTempId(orig.id)) return; // 跳过临时复制的库位

        modifiedLocations.push({
          id: orig.id,
          locationNo: orig.name || orig.locationNo,
          locationName: orig.fullName || orig.locationName,
          x: null,
          y: null,
          z: null,
          width: null,
          height: null,
        });
      });

      const confirmMessage =
        modifiedLocations.length > 0
          ? `确认保存布局？\n\n检测到 ${modifiedLocations.length} 个库位位置发生变化。\n\n点击"确定"保存，点击"取消"继续编辑。`
          : '确认保存布局？\n\n未检测到库位位置变化。\n\n点击"确定"保存当前布局，点击"取消"继续编辑。';

      const ok = await this.myConfirm("保存布局", confirmMessage);
      if (!ok) {
        return;
      }

      const placedLocations = this.inventoryData
        .filter((item) => !isTempId(item.id)) // 排除临时复制的库位
        .map((item) => ({
          id: item.id,
          locationNo: item.name || item.locationNo,
          locationName: item.fullName || item.locationName,
          x: item.x,
          y: item.y,
          z: item.z || 1,
          width: item.w,
          height: item.h,
          palletWeight: item.palletWeight,
          palletHeight: item.palletHeight,
        }));

      const unplacedLocations = this.unplacedLocations
        .filter((u) => !isTempId(u.id)) // 排除临时复制的库位
        .map((u) => ({
          id: u.id,
          locationNo: u.name || u.locationNo,
          locationName: u.fullName || u.locationName,
          x: null,
          y: null,
          z: null,
          width: null,
          height: null,
        }));

      const saveData = {
        bizDept: this.bizDept,
        warehouseId: this.selectedWareId,
        baseCanvasWidth: this.baseCanvasWidth,
        baseCanvasHeight: this.baseCanvasHeight,
        placed: placedLocations,
        unplaced: unplacedLocations,
        modified: modifiedLocations,
        statistics: {
          totalPlaced: placedLocations.length,
          totalUnplaced: unplacedLocations.length,
          modifiedCount: modifiedLocations.length,
        },
      };

      console.log("[Dashboard] 保存布局数据:", saveData);

      try {
        await request.post("/api_hf/uic-system/ware/layout/save", saveData);
        this.myToast("布局已保存成功！", "success");
        this.originalData = JSON.parse(JSON.stringify(this.inventoryData));
      } catch (err) {
        console.error("保存失败", err);
        this.myToast("保存失败，请稍后重试", "error");
      }
    },
    openCopyWareDialog() {
      if (!this.selectedBatchItems.length) return;
      const first = this.selectedBatchItems[0];
      this.copySourceName = first.name || first.locationNo || "";
      const typeLabelMap = {
        0: "仓库",
        1: "库区",
        2: "库位",
        3: "虚拟仓库",
        4: "墙体",
        5: "通道",
        6: "大门",
        7: "柱体",
        8: "电梯",
        9: "楼梯",
        10: "办公区域",
        11: "齐套区",
      };
      // 名称模式：办公区域/齐套区/柱体/电梯/楼梯/大门（可自定义命名）
      const nameModeTypes = [6, 7, 8, 9, 10, 11];
      this.copyWareMode = nameModeTypes.includes(first.wareType)
        ? "name"
        : "count";
      this.copyWareTypeLabel = typeLabelMap[first.wareType] || "元素";
      this.copyWareCount = 1;
      this.copyWareProgress = 0;
      this.copyWareNewName = "";
      this.copyWareDialogVisible = true;
      this.$nextTick(() => {
        const selector =
          this.copyWareMode === "name"
            ? ".copy-ware-modal input[type=text]"
            : ".copy-ware-modal input[type=number]";
        const inp = document.querySelector(selector);
        if (inp) inp.focus();
      });
    },
    async confirmCopyWare() {
      if (this.copyWareLoading) return;
      this.copyWareLoading = true;
      this.copyWareProgress = 0;
      try {
        if (this.copyWareMode === "name") {
          // 名称模式：直接用新名称复制 1 份
          const newName = this.copyWareNewName.trim();
          if (!newName) return;
          await request.post("/api_hf/uic-system/ware/copyWare", null, {
            params: {
              oldWare: this.copySourceName,
              newWare: newName,
            },
          });
          this.myToast(`复制成功！已生成「${newName}」`, "success");
        } else {
          // 数量模式：批量串行复制
          const count = Math.max(1, Number(this.copyWareCount) || 1);
          for (let i = 1; i <= count; i++) {
            const newWare = `${this.copySourceName}_${i}`;
            await request.post("/api_hf/uic-system/ware/copyWare", null, {
              params: {
                oldWare: this.copySourceName,
                newWare: newWare,
              },
            });
            this.copyWareProgress = i;
          }
          this.myToast(
            `复制成功！已生成 ${count} 个${this.copyWareTypeLabel}`,
            "success",
          );
        }
        this.copyWareDialogVisible = false;
        await this.fetchLayout();
      } catch (err) {
        console.error("复制失败", err);
        this.myToast("复制失败：" + (err.message || "请稍后重试"), "error");
      } finally {
        this.copyWareLoading = false;
      }
    },
    openResizeWareDialog() {
      const item = this.selectedBatchItems[0];
      this.resizeWareId = item.id;
      this.resizeWareName = item.name || item.locationNo || "";
      this.resizeWareWeight = item.palletWeight || 1;
      this.resizeWareHeight = item.palletHeight || 1;
      this.resizeWareLoading = false;
      this.resizeWareDialogVisible = true;
    },
    async confirmResizeWare() {
      if (this.resizeWareLoading) return;
      const item = this.inventoryData.find((i) => i.id === this.resizeWareId);
      if (!item) {
        this.myToast("未找到该库位", "error");
        return;
      }
      const newW = Math.max(0.5, this.resizeWareWeight);
      const newH = Math.max(0.5, this.resizeWareHeight);
      item.palletWeight = newW;
      item.palletHeight = newH;
      item.w = newW * this.baseCellW;
      item.h = newH * this.baseCellH;
      // 清除热力图缓存，尺寸变化需要重绘
      delete this.palletStyleCache[item.id];
      if (this.showHeatmap) {
        this.palletStyleCache[item.id] = this.getPalletStyle(item);
      }
      this.resizeWareLoading = true;
      this.resizeWareDialogVisible = false;
      try {
        await this.saveToBackend();
      } catch (err) {
        console.error("保存失败", err);
        this.myToast("保存失败：" + (err.message || "请稍后重试"), "error");
      } finally {
        this.resizeWareLoading = false;
      }
    },
    updateFitScale() {
      if (this.resizing) return;
      this.resizing = true;
      let w, h;
      if (this.isMobile) {
        const el = this.$refs.warehouseViewport;
        if (!el) {
          this.resizing = false;
          return;
        }
        const rect = el.getBoundingClientRect();
        w = Math.max(0, rect.width - 24);
        h = Math.max(0, rect.height - 24);
      }
      if (!w || !h) {
        this.resizing = false;
        return;
      }
      const next = Math.min(
        w / this.baseCanvasWidth,
        h / this.baseCanvasHeight,
      );
      this.fitScale = Number.isFinite(next) && next > 0 ? next : 1;
      setTimeout(() => {
        this.resizing = false;
      }, 50);
    },
    updateCanvasSize() {
      if (this.canvasSizeTimer) clearTimeout(this.canvasSizeTimer);
      this.canvasSizeTimer = setTimeout(() => {
        if (!this.inventoryData.length) {
          this.baseCanvasWidth = this.canvasMinWidth;
          this.baseCanvasHeight = this.canvasMinHeight;
          return;
        }
        let maxX = 0,
          maxY = 0;
        for (const item of this.inventoryData) {
          const right = (Number(item.x) || 0) + (Number(item.w) || 0);
          const bottom = (Number(item.y) || 0) + (Number(item.h) || 0);
          if (right > maxX) maxX = right;
          if (bottom > maxY) maxY = bottom;
        }
        const pad = 100;
        const newW = Math.max(this.canvasMinWidth, maxX + pad);
        const newH = Math.max(this.canvasMinHeight, maxY + pad);
        if (newW === this.baseCanvasWidth && newH === this.baseCanvasHeight)
          return;
        this.baseCanvasWidth = newW;
        this.baseCanvasHeight = newH;
      }, 80);
    },
    fitToContent() {
      if (!this.inventoryData.length) {
        this.resetView();
        return;
      }
      const el = this.$refs.warehouseViewport;
      if (!el) return;
      let viewW, viewH;
      if (this.isMobile) {
        viewW = window.innerHeight - 50;
        viewH = window.innerWidth;
      } else {
        const rect = el.getBoundingClientRect();
        viewW = rect.width;
        // PC 端扣除底部概况栏 56px，保证自适应后画布内容不被遮住
        viewH = rect.height - 56;
      }
      if (viewW < 50 || viewH < 50) {
        setTimeout(() => this.fitToContent(), 200);
        return;
      }
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (const item of this.inventoryData) {
        const x = Number(item.x) || 0,
          y = Number(item.y) || 0;
        const w = Number(item.w) || 0,
          h = Number(item.h) || 0;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x + w > maxX) maxX = x + w;
        if (y + h > maxY) maxY = y + h;
      }
      if (minX === Infinity) {
        this.resetView();
        return;
      }
      const pad = 40;
      const contentW = maxX - minX + pad * 2;
      const contentH = maxY - minY + pad * 2;
      this.fitScale = Math.min(viewW / contentW, viewH / contentH);
      this.userScale = 1;
      this.$nextTick(() => {
        const s = this.effectiveScale;
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        el.scrollLeft = centerX * s - viewW / 2;
        // 滚动中心点上调 22px，让画布内容与底部概况栏对齐
        el.scrollTop = centerY * s - viewH / 2 - 22;
      });
    },
    handleTouchEnd(e, item) {
      if (this.editMode) return;
      const now = Date.now();
      const id = `${item?.name || ""}|${item?.materialNo || ""}`;
      const isDouble =
        this.lastTouchEndId === id && now - this.lastTouchEndTs < 320;
      this.lastTouchEndTs = now;
      this.lastTouchEndId = id;
      if (isDouble) {
        this.showDetail(item);
      }
    },
    handlePanStart(e) {
      if (this.isMobile) return;
      if (this.editMode) {
        if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
          this.startCanvasPan(e);
          return;
        }
        const target = e.target;
        const isCanvasBg =
          target === this.$refs.warehouseViewport ||
          target.classList.contains("warehouse-canvas") ||
          target.classList.contains("warehouse-ground");
        if (e.button === 0 && isCanvasBg) {
          this.startBoxSelect(e);
          return;
        }
        return;
      }
      if (e.button === 0) {
        this.startCanvasPan(e);
      }
    },
    startCanvasPan(e) {
      const el = this.$refs.warehouseViewport;
      if (!el) return;
      try {
        e.preventDefault();
      } catch (_) {
        void 0;
      }
      const startX = e.clientX;
      const startY = e.clientY;
      const startScrollLeft = el.scrollLeft;
      const startScrollTop = el.scrollTop;
      const move = (ev) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        el.scrollLeft = startScrollLeft - dx;
        el.scrollTop = startScrollTop - dy;
      };
      const up = () => {
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", up);
        document.removeEventListener("pointercancel", up);
      };
      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", up);
      document.addEventListener("pointercancel", up);
    },
    boxSelectStart: null,
    boxSelectItems: [],
    handleBoxSelectMove: null,
    handleBoxSelectUp: null,
    startBoxSelect(e) {
      if (!this.editMode) return;
      const viewport = this.$refs.warehouseViewport;
      if (!viewport) return;
      const padLeft = 12;
      const padTop = 12;
      const startX = e.clientX;
      const startY = e.clientY;
      const startVPX =
        startX -
        viewport.getBoundingClientRect().left +
        viewport.scrollLeft -
        padLeft;
      const startVPY =
        startY -
        viewport.getBoundingClientRect().top +
        viewport.scrollTop -
        padTop;
      this.boxSelectStart = { x: startX, y: startY };
      this.selectedBatchItems = [];

      // 预取 inventoryData 引用，避免每次 move 访问 this
      const invData = this.inventoryData;
      let lastThrottle = 0;
      let lastSelected = null;
      let rafId = null;
      let pending = false;

      const overlay = document.createElement("div");
      overlay.style.cssText =
        "position:fixed;border:2px dashed #3b82f6;background:rgba(59,130,246,0.08);pointer-events:none;z-index:9999";
      document.body.appendChild(overlay);

      const syncOverlay = (ev) => {
        // overlay 位置同步更新，无延迟
        const l = Math.min(startX, ev.clientX);
        const t = Math.min(startY, ev.clientY);
        overlay.style.left = l + "px";
        overlay.style.top = t + "px";
        overlay.style.width = Math.abs(ev.clientX - startX) + "px";
        overlay.style.height = Math.abs(ev.clientY - startY) + "px";
      };

      const updateSelection = (ev) => {
        const scale = this.effectiveScale;
        const vx =
          ev.clientX -
          viewport.getBoundingClientRect().left +
          viewport.scrollLeft -
          padLeft;
        const vy =
          ev.clientY -
          viewport.getBoundingClientRect().top +
          viewport.scrollTop -
          padTop;
        const sx1 = Math.min(startVPX, vx) / scale;
        const sy1 = Math.min(startVPY, vy) / scale;
        const sx2 = Math.max(startVPX, vx) / scale;
        const sy2 = Math.max(startVPY, vy) / scale;

        // 用 for 循环找交集，跳过 filter 创建新数组的开销
        const next = [];
        for (let i = 0; i < invData.length; i++) {
          const item = invData[i];
          const ix = Number(item.x) || 0;
          const iy = Number(item.y) || 0;
          const iw = Number(item.w) || 0;
          const ih = Number(item.h) || 0;
          if (ix < sx2 && ix + iw > sx1 && iy < sy2 && iy + ih > sy1) {
            next.push(item);
          }
        }

        // 仅当选中集合变化时才触发 Vue 响应式更新
        if (lastSelected === null) {
          lastSelected = next;
          this.selectedBatchItems = next;
        } else {
          // 快速比较：长度或首元素 id 不同则更新
          const changed =
            next.length !== lastSelected.length ||
            (next.length > 0 &&
              lastSelected.length > 0 &&
              next[0] !== lastSelected[0]);
          if (changed) {
            lastSelected = next;
            this.selectedBatchItems = next;
          }
        }
      };

      // 保存最后一次 move 的 ev，用于节流回调
      let lastEv = e;
      const move = (ev) => {
        lastEv = ev;
        syncOverlay(ev);
        const now = Date.now();
        if (now - lastThrottle < 50) {
          pending = true;
          if (rafId) return;
          rafId = requestAnimationFrame(() => {
            rafId = null;
            if (pending) {
              pending = false;
              lastThrottle = Date.now();
              updateSelection(lastEv);
            }
          });
          return;
        }
        lastThrottle = now;
        updateSelection(ev);
      };

      const up = () => {
        overlay.remove();
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", up);
        document.removeEventListener("pointercancel", up);
        this.boxSelectStart = null;
      };

      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", up);
      document.addEventListener("pointercancel", up);
    },
    batchCopySize() {
      if (this.selectedBatchItems.length < 2) return;
      const ref = this.selectedBatchItems[0];
      const refW = Number(ref.w) || 140;
      const refH = Number(ref.h) || 80;
      const batchIds = new Set(this.selectedBatchItems.map((i) => i.id));
      for (let i = 1; i < this.selectedBatchItems.length; i++) {
        const item = this.selectedBatchItems[i];
        const canFit = !this.checkOverlap(
          item,
          refW,
          refH,
          item.x,
          item.y,
          batchIds,
        );
        if (canFit) {
          item.w = refW;
          item.h = refH;
        }
      }
      this.updateCanvasSize();
    },
    batchReturnToUnplaced() {
      if (!this.editMode || !this.selectedBatchItems.length) return;
      const ids = new Set(this.selectedBatchItems.map((i) => i.id));
      const toRemove = this.inventoryData.filter((i) => ids.has(i.id));
      for (const item of toRemove) {
        this.unplacedLocations.unshift({
          id: item.id,
          name: item.name || "",
          fullName: item.fullName || "",
          materialNo: item.materialNo,
          quantity: item.quantity,
          z: null,
          x: null,
          y: null,
          w: null,
          h: null,
          width: null,
          height: null,
          pallet_weight: item.palletWeight || item.pallet_weight || null,
          pallet_height: item.palletHeight || item.pallet_height || null,
          wareType: item.wareType || 2,
        });
      }
      this.inventoryData = this.inventoryData.filter((i) => !ids.has(i.id));
      this._clearPassageBorderCache();
      this.selectedBatchItems = [];
      this.updateCanvasSize();
      this.myToast(`已退回 ${toRemove.length} 个库位`, "success");
    },
    highlightByPrefix() {
      if (!this.editMode) return;
      const prefix = (this.editSearchPrefix || "").trim().toUpperCase();
      if (!prefix) {
        this.highlightedLocations = new Set();
        return;
      }
      const matches = new Set();
      for (const item of this.inventoryData) {
        const name = (item.name || "").toUpperCase();
        if (name.startsWith(prefix)) {
          matches.add(item.name);
        }
      }
      this.highlightedLocations = matches;
      this.$nextTick(() => this.drawAllPallets());
    },
    clearEditSearch() {
      this.editSearchPrefix = "";
      this.highlightedLocations = new Set();
    },
    selectHighlighted() {
      if (!this.highlightedLocations.size) return;
      const matched = this.inventoryData.filter((item) =>
        this.highlightedLocations.has(item.name),
      );
      const existingIds = new Set(this.selectedBatchItems.map((i) => i.id));
      for (const item of matched) {
        if (!existingIds.has(item.id)) {
          this.selectedBatchItems.push(item);
        }
      }
      this.highlightedLocations = new Set();
      this.editSearchPrefix = "";
      this.myToast(`已选中 ${matched.length} 个库位`, "success");
    },
    checkOverlap(excludeItem, newW, newH, newX, newY, excludeIds) {
      const idSet =
        excludeIds instanceof Set
          ? excludeIds
          : excludeIds != null
          ? new Set([excludeIds])
          : new Set();
      const items = this.inventoryData.filter(
        (i) => !idSet.has(i.id) && i !== excludeItem,
      );
      const isGate = excludeItem?.wareType === 6;
      for (const other of items) {
        const ox = Number(other.x) || 0;
        const oy = Number(other.y) || 0;
        const ow = Number(other.w) || 0;
        const oh = Number(other.h) || 0;
        if (isGate && other.wareType === 4) continue;
        if (
          newX < ox + ow &&
          newX + newW > ox &&
          newY < oy + oh &&
          newY + newH > oy
        ) {
          return true;
        }
      }
      return false;
    },
    batchAlignH() {
      if (this.selectedBatchItems.length < 2) return;
      const minY = Math.min(
        ...this.selectedBatchItems.map((i) => Number(i.y) || 0),
      );
      for (const item of this.selectedBatchItems) item.y = minY;
      this.updateCanvasSize();
    },
    batchAlignV() {
      if (this.selectedBatchItems.length < 2) return;
      const minX = Math.min(
        ...this.selectedBatchItems.map((i) => Number(i.x) || 0),
      );
      for (const item of this.selectedBatchItems) item.x = minX;
      this.updateCanvasSize();
    },
    batchPackH() {
      if (this.selectedBatchItems.length < 2) return;
      const minX = Math.min(
        ...this.selectedBatchItems.map((i) => Number(i.x) || 0),
      );
      const sorted = [...this.selectedBatchItems].sort(
        (a, b) => (Number(a.y) || 0) - (Number(b.y) || 0),
      );
      let curY = Number(sorted[0].y) || 0;
      for (const item of sorted) {
        item.x = minX;
        item.y = curY;
        curY += Number(item.h) || 0;
      }
      this.updateCanvasSize();
    },
    batchPackV() {
      if (this.selectedBatchItems.length < 2) return;
      const minY = Math.min(
        ...this.selectedBatchItems.map((i) => Number(i.y) || 0),
      );
      const sorted = [...this.selectedBatchItems].sort(
        (a, b) => (Number(a.x) || 0) - (Number(b.x) || 0),
      );
      let curX = Number(sorted[0].x) || 0;
      for (const item of sorted) {
        item.y = minY;
        item.x = curX;
        curX += Number(item.w) || 0;
      }
      this.updateCanvasSize();
    },
    batchDistributeH() {
      if (this.selectedBatchItems.length < 3) return;
      const sorted = [...this.selectedBatchItems].sort(
        (a, b) => (Number(a.x) || 0) - (Number(b.x) || 0),
      );
      const first = Number(sorted[0].x) || 0;
      const last = Number(sorted[sorted.length - 1].x) || 0;
      const gap = (last - first) / (sorted.length - 1);
      sorted.forEach((item, i) => {
        item.x = first + gap * i;
      });
    },
    batchDistributeV() {
      if (this.selectedBatchItems.length < 3) return;
      const sorted = [...this.selectedBatchItems].sort(
        (a, b) => (Number(a.y) || 0) - (Number(b.y) || 0),
      );
      const first = Number(sorted[0].y) || 0;
      const last = Number(sorted[sorted.length - 1].y) || 0;
      const gap = (last - first) / (sorted.length - 1);
      sorted.forEach((item, i) => {
        item.y = first + gap * i;
      });
    },
    batchSortByName() {
      if (this.selectedBatchItems.length < 2) return;
      const positions = this.selectedBatchItems.map((item) => ({
        x: Number(item.x) || 0,
        y: Number(item.y) || 0,
      }));
      positions.sort((a, b) => a.y - b.y || a.x - b.x);
      const sorted = [...this.selectedBatchItems].sort((a, b) => {
        const na = (a.name || a.locationNo || "").toString();
        const nb = (b.name || b.locationNo || "").toString();
        return na.localeCompare(nb, "zh-CN", { numeric: true });
      });
      if (this.batchSortDesc) sorted.reverse();
      this.batchSortDesc = !this.batchSortDesc;
      for (let i = 0; i < sorted.length; i++) {
        sorted[i].x = positions[i].x;
        sorted[i].y = positions[i].y;
      }
      this.updateCanvasSize();
      this.myToast(
        `已${this.batchSortDesc ? "正序" : "倒序"}排列 ${sorted.length} 个库位`,
        "success",
      );
    },
    batchRotate() {
      if (!this.selectedBatchItems.length) return;
      for (const item of this.selectedBatchItems) {
        const tmp = item.palletWeight;
        item.palletWeight = item.palletHeight;
        item.palletHeight = tmp;
        item.w = item.palletWeight * this.baseCellW;
        item.h = item.palletHeight * this.baseCellH;
      }
      this.updateCanvasSize();
      this.myToast(
        `已旋转 ${this.selectedBatchItems.length} 个库位`,
        "success",
      );
    },
    myConfirm(title, message) {
      return new Promise((resolve) => {
        this.modalType = "confirm";
        this.modalTitle = title;
        this.modalMessage = message;
        this.modalResolve = resolve;
        this.modalVisible = true;
      });
    },
    myAlert(title, message) {
      return new Promise((resolve) => {
        this.modalType = "alert";
        this.modalTitle = title;
        this.modalMessage = message;
        this.modalResolve = resolve;
        this.modalVisible = true;
      });
    },
    myToast(message, type) {
      this.toastType = type || "success";
      this.toastMessage = message;
      this.toastVisible = true;
      setTimeout(() => {
        this.toastVisible = false;
      }, 2500);
    },
    modalOk() {
      this.modalVisible = false;
      if (this.modalResolve) this.modalResolve(true);
      this.modalResolve = null;
    },
    modalCancel() {
      this.modalVisible = false;
      if (this.modalResolve) this.modalResolve(false);
      this.modalResolve = null;
    },
  },
  watch: {
    inventoryData() {
      this._clearPassageBorderCache();
    },
    showHeatmap(val) {
      if (val) {
        // 开启热力图时清除点亮状态，避免冲突
        if (this.highlightedLocations.size > 0) {
          this.highlightedLocations = new Set();
          this.editSearchPrefix = "";
        }
        this.$nextTick(() => this.drawAllPallets());
      } else {
        this.$nextTick(() => this.drawAllPallets());
      }
    },
    highlightedLocations: {
      handler() {
        this.$nextTick(() => this.drawAllPallets());
      },
      deep: true,
    },
  },
  created() {
    this.originalData = JSON.parse(JSON.stringify(this.inventoryData));
    const userInfo = getUicAuthUserInfo();
    this.bizDept = userInfo?.deptId || "";
    this.stockId = "";
  },
  mounted() {
    this.updateFitScale();
    this.fetchWareList();
    const viewport = this.$refs.warehouseViewport;
    if (viewport) {
      viewport.addEventListener("wheel", this.handleWheel, { passive: false });
      viewport.addEventListener("pointerdown", this.handlePanStart);
    }
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeDebounceFlag) return;
        this.resizeDebounceFlag = true;
        setTimeout(() => {
          this.resizeDebounceFlag = false;
          this.updateFitScale();
          if (!this.fitDone && this.inventoryData.length) {
            const el = this.$refs.warehouseViewport;
            if (el && el.getBoundingClientRect().width > 50) {
              this.fitDone = true;
              this.fitToContent();
            }
          }
        }, 100);
      });
      if (this.$refs.warehouseViewport)
        this.resizeObserver.observe(this.$refs.warehouseViewport);
      if (this.$refs.warehouseBox)
        this.resizeObserver.observe(this.$refs.warehouseBox);
    } else {
      window.addEventListener("resize", this.updateFitScale);
    }
  },
  beforeUnmount() {
    if (this.renderTimer) cancelAnimationFrame(this.renderTimer);
    const viewport = this.$refs.warehouseViewport;
    if (viewport) {
      viewport.removeEventListener("wheel", this.handleWheel);
      viewport.removeEventListener("pointerdown", this.handlePanStart);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    } else {
      window.removeEventListener("resize", this.updateFitScale);
    }
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, "Microsoft YaHei", "Segoe UI", sans-serif;
}

.warehouse-app {
  width: 100vw;
  height: 100vh;
  background: #ffffff;
  color: #cbd5e1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.top-bar {
  height: 52px;
  padding: 0 18px;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 14px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  z-index: 30;
}

.tb-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.logo-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1c45cd;
  border-radius: 0;
  color: #fff;
  border: none;
}

.logo-title {
  font-size: 14px;
  color: #1c45cd;
  font-weight: 700;
  letter-spacing: 1.5px;
  white-space: nowrap;
}

.tb-center {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.ware-select {
  flex: 0 0 auto;
  width: 180px;
  height: 36px;
  padding: 0 12px;
  background: rgba(255, 255, 255);
  border: 1px solid rgba(59, 130, 246);
  border-radius: 0;
  color: #000000;
  outline: none;
  font-size: 13px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.ware-select:focus {
  border-color: #000000;
}

.ware-select option {
  background: #ffffff;
  color: #1e293b;
}

.search-trigger {
  flex: 1;
  min-width: 0;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  background: #1d4ed8;
  border: none;
  border-radius: 0;
  cursor: pointer;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  border: 1px solid rgba(96, 165, 250, 0.2);
}

.search-trigger:hover {
  background: #000000;
  color: #ffffff;
}

.search-icon {
  flex-shrink: 0;
  color: #64748b;
}

.search-placeholder {
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tb-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.edit-btn {
  padding: 6px 14px;
  background: #ffffff;
  border: 1px solid #000000;
  color: #050505;
  border-radius: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.edit-btn:hover {
  background: #001d57;
  border-color: #f7f6f6;
  color: #ffffff;
}

.edit-btn.active {
  background: #dc2626;
  color: #fff;
  font-weight: 700;
  border-color: #b91c1c;
}

.save-btn {
  padding: 6px 14px;
  background: #16a34a;
  border: none;
  color: #fff;
  font-weight: 700;
  border-radius: 0;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
}

.save-btn:hover {
  background: #15803d;
}

.save-btn:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}
.copy-source {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
  padding: 6px 0;
}
.modal-field {
  margin-bottom: 16px;
}
.modal-field-hint {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  word-break: break-all;
  line-height: 1.5;
}
.modal-field-row {
  display: flex;
  gap: 16px;
}
.modal-field-half {
  flex: 1;
  min-width: 0;
}
.modal-field-half .modal-field-label {
  display: block;
  margin-bottom: 6px;
}
.modal-field:last-child {
  margin-bottom: 0;
}
.modal-field-label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
  font-weight: 500;
}
.modal-field-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  font-size: 14px;
  color: #111827;
  background: #fff;
  outline: none;
}
.modal-field-input:focus {
  border-color: #1d4ed8;
}

.canvas-area {
  position: fixed;
  top: 52px;
  left: 0;
  right: 0;
  bottom: 56px;
  overflow: hidden;
  background: #ffffff;
}

.warehouse-viewport {
  position: absolute;
  inset: 0;
  padding: 12px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-x pan-y;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}
.warehouse-viewport::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.warehouse-viewport::-webkit-scrollbar-track {
  background: transparent;
}
.warehouse-viewport::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 0;
}
.warehouse-viewport::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

.warehouse-canvas {
  position: relative;
  transform-origin: top left;
  overflow: visible;
  background-image: linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  background-color: #ffffff;
  min-width: 100%;
  min-height: 100%;
}

.warehouse-ground {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(rgba(0, 0, 0, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.04) 1px, transparent 1px);
  background-size: 20px 20px;
  background-color: #ffffff;
}

.guide-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 3;
}

.guide-line {
  position: absolute;
  opacity: 0.9;
}

.guide-line-v {
  top: 0;
  width: 0;
  border-left: 1px dashed rgba(245, 158, 11, 0.95);
}

.guide-line-h {
  left: 0;
  height: 0;
  border-top: 1px dashed rgba(245, 158, 11, 0.95);
}

.canvas-stats {
  position: absolute;
  top: 8px;
  left: 10px;
  background: #ffffff;
  color: #6b7280;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 0;
  z-index: 10;
  pointer-events: none;
  border: 1px solid #e5e7eb;
}

.canvas-empty {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #475569;
  font-size: 14px;
  pointer-events: none;
}

.location {
  position: absolute;
  background: #ffffff;
  color: #000000;
  border-radius: 0;
  border: 1px solid #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: var(--loc-font-size, 13px);
  overflow: hidden;
  user-select: none;
  z-index: 2;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

.location:hover {
  /* no hover effect */
}

.location.highlight {
  color: #fff !important;
}
.location:not(.heatmap-mode).highlight {
  background: #dc2626 !important;
  border: 1px solid #000000 !important;
  color: #ffffff !important;
  border-radius: 0;
}

.location.batch-selected {
  background: rgba(59, 130, 246, 0.15) !important;
  border: 1px solid #000000 !important;
  color: #1e40af !important;
  border-radius: 0;
}

/* ware_type 类型样式 */
.type-0 {
  background: #ffffff;
  border-color: #000000;
}
.type-1 {
  background: #ffffff;
  border-color: #000000;
  border-style: dashed;
}
.type-2 {
  background: #ffffff;
  border-color: #000000;
}
.type-3 {
  background: #ffffff;
  border-color: #000000;
  border-style: dashed;
}
.type-4 {
  background: rgba(0, 0, 0);
  border: 2px solid rgba(0, 0, 0);
  cursor: default;
  z-index: 3;
  overflow: visible;
  color: #ffffff;
}
.wall-cover {
  position: absolute;
  inset: -2px;
  pointer-events: none;
  z-index: 10;
}
.type-5 {
  background: #00833e;
  border: none;
  cursor: default;
  z-index: 1;
  overflow: visible;
  color: #ffffff;
}
.type-5::before {
  content: "";
  position: absolute;
  inset: 0;
  border: 4px solid rgba(234, 179, 8);
  pointer-events: none;
}
.type-5::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.passage-cover {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
}
.wall-cover {
  z-index: 10 !important;
}
.type-5::after {
  content: "";
  position: absolute;
  inset: -6px;
  pointer-events: none;
  background-image: var(--p-bottom-bg, none), var(--p-top-bg, none);
  background-position: var(--p-bottom-offset, 6px) calc(100% - 12px),
    var(--p-top-offset, 6px) 6px;
  background-size: var(--p-bottom-size, 0) 12px, var(--p-top-size, 0) 12px;
  background-repeat: no-repeat, no-repeat;
}
.type-6 {
  border: 3px dashed rgba(0, 0, 0);
  display: flex;
  flex-direction: var(--gate-dir, row);
  align-items: center;
  justify-content: space-between;
  padding: 6px;
  z-index: 4;
}
.type-6::before,
.type-6::after {
  content: "";
  width: var(--gate-box, 16px);
  height: var(--gate-box, 16px);
  border: 2px solid rgba(0, 0, 0);
  flex-shrink: 0;
  pointer-events: none;
  z-index: 0;
}
.type-7 {
  background: rgba(75, 85, 99, 0.2);
  border: 2px solid #000000;
  border-radius: 0;
  cursor: default;
}
.type-8 {
  background: transparent;
  border: 3px solid rgba(245, 158, 11, 0.8);
  padding: 6px;
}
.type-8::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
      to bottom right,
      transparent calc(50% - 1px),
      rgba(245, 158, 11, 0.6) calc(50% - 1px),
      rgba(245, 158, 11, 0.6) calc(50% + 1px),
      transparent calc(50% + 1px)
    ),
    linear-gradient(
      to bottom left,
      transparent calc(50% - 1px),
      rgba(245, 158, 11, 0.6) calc(50% - 1px),
      rgba(245, 158, 11, 0.6) calc(50% + 1px),
      transparent calc(50% + 1px)
    );
  pointer-events: none;
  z-index: 0;
}
.type-8::after {
  content: "";
  position: absolute;
  inset: 5px;
  border: 2px dashed rgba(245, 158, 11, 0.5);
  pointer-events: none;
  z-index: 1;
}
.type-9 {
  background: transparent;
  border: 3px solid rgba(0, 0, 0);
  padding: 6px;
}
.type-9::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
      to right,
      transparent calc(50% - 1px),
      rgba(9, 9, 9) calc(50% - 1px),
      rgba(0, 0, 0) calc(50% + 1px),
      transparent calc(50% + 1px)
    ),
    repeating-linear-gradient(
      to bottom,
      transparent,
      transparent 8px,
      rgba(1, 1, 1) 8px,
      rgba(1, 1, 1) 10px
    );
  pointer-events: none;
  z-index: 0;
}
.type-10 {
  background: transparent;
  border: 3px solid rgba(0, 0, 0);
  padding: 6px;
}
.type-10::after {
  content: "";
  position: absolute;
  inset: 4px;
  border: 2px solid rgba(0, 0, 0);
  pointer-events: none;
  z-index: 0;
}
.type-11 {
  background: transparent;
  border: 2px solid rgb(177, 94, 0);
  padding: 6px;
}
.type-11::after {
  content: "";
  position: absolute;
  inset: 3px;
  border: 2px dashed rgb(177, 94, 0);
  pointer-events: none;
  z-index: 0;
}

.location.heatmap-mode {
  background: rgb(
    var(--heat-r, 255),
    var(--heat-g, 255),
    var(--heat-b, 255)
  ) !important;
  border-color: #000000 !important;
  border-radius: 0;
}
.location.heatmap-mode.overstock {
  border-color: #000000 !important;
  border-width: 1px !important;
}
.location.heatmap-mode:hover {
  z-index: 10;
}

.pallet-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.rotate-btn {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  color: #000000;
  font-size: 12px;
  cursor: pointer;
  z-index: 5;
  opacity: 0;
}
.location:hover .rotate-btn {
  opacity: 1;
}
.rotate-btn:hover {
  background: #000000;
  color: #ffffff;
}

.resize-handle {
  position: absolute;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  opacity: 0;
}
.location:hover .resize-handle {
  opacity: 1;
}
.resize-handle:hover {
  background: #f3f4f6;
}
/* 四边把手 */
.resize-handle-n,
.resize-handle-s {
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 6px;
  cursor: ns-resize;
}
.resize-handle-n {
  top: 0;
}
.resize-handle-s {
  bottom: 0;
}
.resize-handle-e,
.resize-handle-w {
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 20px;
  cursor: ew-resize;
}
.resize-handle-e {
  right: 0;
}
.resize-handle-w {
  left: 0;
}
/* 四角把手 */
.resize-handle-nw,
.resize-handle-ne,
.resize-handle-sw,
.resize-handle-se {
  width: 12px;
  height: 12px;
}
.resize-handle-nw {
  top: 0;
  left: 0;
  cursor: nw-resize;
}
.resize-handle-ne {
  top: 0;
  right: 0;
  cursor: ne-resize;
}
.resize-handle-sw {
  bottom: 0;
  left: 0;
  cursor: sw-resize;
}
.resize-handle-se {
  bottom: 0;
  right: 0;
  cursor: se-resize;
}

.heatmap-btn {
  padding: 6px 14px;
  background: #ffffff;
  border: 1px solid #000000;
  color: #0a0a0a;
  border-radius: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.heatmap-btn:hover {
  background: #000000;
  border-color: #fafafa;
  color: #ffffff;
}
.heatmap-btn.active {
  background: #1d4ed8;
  color: #fff;
  font-weight: 700;
  border-color: #1e40af;
}

.loc-name {
  font-weight: 700;
  white-space: normal;
  max-width: 100%;
  text-align: center;
  line-height: 1.1;
  padding: 0 2px;
  overflow: hidden;
  position: relative;
  z-index: 1;
  color: var(--loc-text-color, inherit);
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.loc-name.vertical-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  max-height: 100%;
  overflow: visible;
}

.float-zoom {
  position: absolute;
  top: 8px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 3px;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  padding: 4px 6px;
  z-index: 10;
}

.fb-btn {
  width: 30px;
  height: 28px;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.3);
  color: #000000;
  border-radius: 0;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fb-btn:hover {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(1, 52, 133, 0.5);
  color: #00377a;
}

.fb-zoom {
  font-size: 14px;
  color: #000000;
  min-width: 40px;
  text-align: center;
  font-weight: 600;
}

.fb-divider {
  width: 1px;
  height: 18px;
  background: rgba(59, 130, 246, 0.15);
  margin: 0 2px;
}

.fb-btn-text {
  width: auto;
  min-width: 36px;
  padding: 0 8px;
  font-size: 11px;
  height: 28px;
}

.float-batch {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  padding: 6px 14px;
  z-index: 10;
}

.batch-count {
  color: #d97b00;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.fb-btn-danger {
  color: #ef4444 !important;
}
.fb-btn-danger:hover {
  background: rgba(239, 68, 68, 0.2) !important;
}

.drawer-highlight-wrap {
  border: 1px solid #000000;
}

.highlight-label {
  color: #050505;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.highlight-input {
  width: 70px;
}

.highlight-select-btn {
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  color: #000000;
  font-size: 11px;
  padding: 2px 6px;
  cursor: pointer;
  white-space: nowrap;
}
.highlight-select-btn:hover {
  background: #000000;
  color: #ffffff;
}

.drawer-search-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.drawer-search-row .drawer-search-wrap {
  flex: 1;
  margin-bottom: 0;
}

.bottom-drawer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 56px; /* 底部概况栏上方 */
  background: #ffffff;
  border-top: 1px solid #00349d;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 240px;
  z-index: 40;
}

.bottom-drawer.collapsed {
  max-height: 40px;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  cursor: pointer;
  user-select: none;
}

.drawer-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drawer-header:hover {
  background: #f9fafb;
}

.batch-place-btn {
  padding: 3px 10px;
  background: rgba(22, 163, 74, 0.08);
  border: 1px solid rgba(22, 163, 74, 0.3);
  color: #16a34a;
  border-radius: 0;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.batch-place-btn:hover {
  background: rgba(22, 163, 74, 0.15);
  color: #15803d;
}
.sort-btn {
  padding: 3px 8px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #1d4ed8;
  border-radius: 0;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.sort-btn:hover {
  background: rgba(59, 130, 246, 0.15);
  color: #1e40af;
}
.drawer-search-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f9fafb;
  border: 1px solid #000000;
  border-radius: 0;
  margin-bottom: 8px;
}
.drawer-search-input {
  flex: 1;
  background: none;
  border: none;
  color: #060606;
  font-size: 12px;
  outline: none;
}
.drawer-search-input::placeholder {
  color: #64748b;
}
.drawer-search-clear {
  color: #64748b;
  cursor: pointer;
  font-size: 12px;
  padding: 2px;
}
.drawer-search-clear:hover {
  color: #94a3b8;
}

.drawer-title {
  font-size: 13px;
  font-weight: 600;
  color: #002e63;
}

.drawer-arrow {
  font-size: 12px;
  color: #64748b;
}

.drawer-type-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  max-height: 80px;
  overflow-y: auto;
}
.type-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.type-tag:hover {
  opacity: 0.8;
}
.type-tag .type-count {
  font-size: 10px;
  opacity: 0.7;
}
.drawer-body {
  padding: 0 16px 6px;
}

.drawer-hint {
  padding: 10px 12px;
  border-radius: 0;
  background: rgba(148, 163, 184, 0.06);
  border: 1px dashed rgba(148, 163, 184, 0.25);
  color: rgba(226, 232, 240, 0.8);
  font-size: 12px;
  text-align: center;
}

.drawer-empty {
  padding: 10px;
  color: #64748b;
  font-size: 13px;
  text-align: center;
}

.drawer-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(59, 130, 246, 0.3) transparent;
}

.drawer-scroll::-webkit-scrollbar {
  height: 6px;
}

.drawer-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.drawer-scroll::-webkit-scrollbar-thumb {
  background: rgba(59, 130, 246, 0.3);
  border-radius: 3px;
}

.drawer-item {
  flex: 0 0 auto;
  width: 140px;
  padding: 10px 12px;
  border-radius: 0;
  background: #ffffff;
  border: 1px solid #000000;
  cursor: grab;
}

.drawer-item:hover {
  background: #f3f4f6;
  border-color: #000000;
}

.drawer-item:active {
  cursor: grabbing;
}

.drawer-item.disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.drawer-item-name {
  font-size: 13px;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-item-sub {
  margin-top: 4px;
  font-size: 11px;
  color: #000000;
}

.search-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
}

.search-panel {
  pointer-events: auto;
  width: min(360px, 75vw);
  height: 100%;
  background: #ffffff;
  border-left: 1px solid #000000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sp-supplier-section {
  flex-shrink: 0;
  border-bottom: 1px solid #000000;
}
.sp-material-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.sp-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px 0;
}
.sp-section-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.sp-section-count {
  font-size: 11px;
  color: #64748b;
}
.sp-clear-btn {
  font-size: 11px;
  color: #c40000;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.sp-clear-btn:hover {
  text-decoration: underline;
}
.sp-search-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 6px 14px 8px;
  background: rgba(255, 255, 255);
  border: 1px solid rgb(7, 7, 7);
  border-radius: 6px;
  padding: 0 8px;
  height: 30px;
}
.sp-search-inline:focus-within {
  border-color: #3b82f6;
}
.sp-input-inline {
  flex: 1;
  background: none;
  border: none;
  color: #000000;
  font-size: 12px;
  outline: none;
  min-width: 0;
}
.sp-input-inline::placeholder {
  color: #070707;
  font-size: 12px;
}
.sp-supplier-list {
  max-height: 160px;
  overflow-y: auto;
  padding: 0 14px 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255) transparent;
}
.sp-material-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 14px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}
.sp-material-toolbar {
  padding: 6px 14px;
  display: flex;
  justify-content: center;
  gap: 10px;
}
.sp-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}
.sp-items::-webkit-scrollbar {
  width: 4px;
}
.sp-items::-webkit-scrollbar-track {
  background: transparent;
}
.sp-items::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}
.sp-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}
.sp-item:hover {
  background: #f3f4f6;
}
.sp-item-active {
  background: #f3f4f6;
  border-left: 2px solid #3b82f6;
  padding-left: 8px;
}
.sp-item-name {
  font-size: 13px;
  font-weight: 500;
  color: #000000;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.sp-item-arrow {
  color: #64748b;
  font-size: 16px;
  flex-shrink: 0;
}
.sp-material-item {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}
.sp-mat-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  text-align: left;
}
.sp-mat-code {
  font-size: 13px;
  font-weight: 700;
  color: #001e95;
  text-align: left;
}
.sp-mat-sup {
  font-size: 11px;
  color: #bd6500;
  font-weight: 500;
}
.sp-mat-desc {
  font-size: 11px;
  color: #374151;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: left;
}
.sp-footer {
  flex-shrink: 0;
  padding: 8px 14px;
  border-top: 1px solid #000000;
}
.sp-close-btn {
  width: 100%;
  padding: 8px;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  color: #000000;
  font-size: 13px;
  cursor: pointer;
}
.sp-close-btn:hover {
  background: #000000;
  color: #ffffff;
}
.sp-hint-sm {
  padding: 8px;
  text-align: center;
  color: #374151;
  font-size: 12px;
}
.sp-tool-btn {
  width: 100%;
  padding: 7px 12px;
  background: rgb(255, 255, 255);
  border: 1px solid rgba(245, 158, 11);
  border-radius: 6px;
  color: #fe8c00;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.sp-tool-btn:hover {
  background: rgba(245, 158, 11, 0.2);
}
.sp-tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.sp-cancel-btn {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68);
  color: #f87171;
  margin-left: 8px;
  width: auto;
}
.sp-cancel-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.dialog-box {
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  width: min(560px, calc(100vw - 32px));
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #000000;
}

.dialog-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dialog-title-wrap h3 {
  color: #000000;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.dialog-location-name {
  color: #374151;
  font-size: 13px;
}

.dialog-close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  color: #000000;
  cursor: pointer;
  font-size: 14px;
}

.dialog-close-btn:hover {
  background: #000000;
  color: #ffffff;
}

.dialog-body {
  flex: 1;
  padding: 16px 20px;
  overflow-y: auto;
}

.detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: #374151;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-top-color: #000000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.detail-error {
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 0;
  color: #dc2626;
  text-align: center;
}

.detail-empty {
  padding: 32px;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 0;
  color: #374151;
  font-size: 13px;
  text-align: center;
}

.supplier-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.supplier-block {
  border-radius: 0;
  background: #ffffff;
  border: 1px solid #d1d5db;
  overflow: hidden;
}

.supplier-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #f3f4f6;
  cursor: pointer;
}

.supplier-header:hover {
  background: #e5e7eb;
}

.supplier-title {
  color: #000000;
  font-weight: 600;
  font-size: 14px;
}

.supplier-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.supplier-count {
  color: #374151;
  font-size: 12px;
}

.supplier-arrow {
  color: #000000;
  font-size: 10px;
}

.material-list {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.material-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0;
}

.material-item:hover {
  border-color: #000000;
  background: #f9fafb;
}

.material-item.active {
  border-color: #000000;
  background: #e5e7eb;
}

.material-info {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.material-no {
  color: #000000;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}

.material-desc {
  color: #374151;
  font-size: 12px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
}

.material-stock {
  display: flex;
  gap: 12px;
  margin-left: 16px;
  flex-shrink: 0;
}

.stock-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stock-num {
  font-size: 14px;
  font-weight: 700;
}

.stock-label {
  font-size: 10px;
  color: #374151;
}

.stock-good .stock-num {
  color: #16a34a;
}

.stock-bad .stock-num {
  color: #dc2626;
}

.dialog-footer {
  padding: 16px 20px;
  border-top: 1px solid #000000;
  background: #f9fafb;
}

.dialog-footer-btn {
  width: 100%;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  color: #000000;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.dialog-footer-btn:hover {
  background: #000000;
  color: #ffffff;
}

.material-value {
  flex: 1 1 auto;
  font-size: 13px;
  font-weight: 600;
  color: #e0f2fe;
  text-align: right;
  word-break: break-all;
}

.material-stock {
  font-weight: 700;
  color: #6ee7b7;
}

.mobile-search-btn {
  padding: 8px 16px;
  background: #ffffff;
  border: 1px solid #000000;
  color: #000000;
  border-radius: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.mobile-search-btn:active {
  background: #f3f4f6;
}

.mobile-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
}

.mobile-drawer {
  width: 100%;
  max-height: 78vh;
  background: #ffffff;
  border-top: 1px solid #000000;
  padding: 16px;
  overflow: auto;
  border-radius: 0;
}

.mobile-drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.mobile-drawer-title {
  color: #000000;
  font-weight: 600;
  font-size: 16px;
}

.mobile-drawer-close {
  padding: 6px 12px;
  background: #ffffff;
  border: 1px solid #000000;
  color: #000000;
  border-radius: 0;
  cursor: pointer;
  font-size: 13px;
}

.mobile-drawer-close:hover {
  background: #000000;
  color: #ffffff;
}

.search-section {
  margin-bottom: 16px;
}

.search-section h3 {
  color: #000000;
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 600;
}

.ware-select-row {
  margin-bottom: 12px;
}

.search-input {
  width: 100%;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  color: #000000;
  outline: none;
  font-size: 13px;
}

.search-input:focus {
  border-color: #000000;
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-hint,
.search-empty {
  margin-top: 10px;
  padding: 14px;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  color: #374151;
  font-size: 13px;
  text-align: center;
}

.result-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
  max-height: 220px;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}
.result-list::-webkit-scrollbar {
  width: 4px;
}
.result-list::-webkit-scrollbar-track {
  background: transparent;
}
.result-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  cursor: pointer;
}

.result-item:hover {
  background: #f3f4f6;
  border-color: #000000;
}

.result-main {
  min-width: 0;
}

.result-title {
  font-size: 13px;
  font-weight: 600;
  color: #000000;
  text-align: left;
}

.result-sub {
  margin-top: 3px;
  font-size: 12px;
  color: #374151;
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
}

.result-supplier {
  margin-top: 4px;
  font-size: 11px;
  color: #000000;
  text-align: left;
  font-weight: 500;
}

.result-stock {
  flex: 0 0 auto;
  padding: 5px 10px;
  background: #ffffff;
  border: 1px solid #000000;
  color: #000000;
  font-weight: 700;
  font-size: 13px;
}

.modal-box {
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  padding: 24px;
  min-width: 280px;
  max-width: 360px;
  text-align: center;
}

.modal-box-title {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 10px;
}

.modal-box-body {
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  margin-bottom: 20px;
  white-space: pre-line;
}

.modal-box-btns {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.modal-btn {
  padding: 8px 24px;
  border: 1px solid #000000;
  border-radius: 0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.modal-btn-cancel {
  background: #ffffff;
  color: #000000;
}

.modal-btn-cancel:hover {
  background: #f3f4f6;
}

.modal-btn-ok {
  background: #000000;
  color: #ffffff;
}

.modal-btn-ok:hover {
  background: #374151;
}

.area-panel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
  display: flex;
  justify-content: flex-start;
}
.area-panel {
  width: 540px;
  max-width: 94vw;
  height: 100%;
  background: #ffffff;
  border-right: 1px solid #000000;
  display: flex;
  flex-direction: column;
}
@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}
.area-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #000000;
  flex-shrink: 0;
}
.area-panel-title {
  color: #000000;
  font-size: 15px;
  font-weight: 600;
}
.area-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}
.area-refresh-btn {
  background: none;
  border: 1px solid #000000;
  color: #000000;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  border-radius: 0;
}
.area-refresh-btn:hover {
  color: #ffffff;
  background: #000000;
}
.area-panel-close {
  background: none;
  border: 1px solid #000000;
  color: #000000;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
}
.area-panel-close:hover {
  background: #000000;
  color: #ffffff;
}
.area-loading {
  color: #374151;
  text-align: center;
  padding: 60px 0;
}
.area-panel-top {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 20px 16px;
  flex-shrink: 0;
}
.area-ring-wrap {
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}
.area-ring-svg {
  width: 100%;
  height: 100%;
}
.area-ring-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.area-ring-rate {
  font-size: 20px;
  font-weight: 700;
  color: #000000;
  line-height: 1;
}
.area-ring-label {
  font-size: 10px;
  color: #000000;
  margin-top: 2px;
}
.area-summary-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.area-stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  padding: 10px 14px;
}
.area-stat-label {
  color: #000000;
  font-size: 12px;
}
.area-stat-value {
  color: #000000;
  font-size: 14px;
  font-weight: 600;
}

.area-filter-row {
  display: flex;
  gap: 0;
  padding: 0 20px 12px;
  flex-shrink: 0;
}
.area-filter-btn {
  padding: 7px 14px;
  background: #ffffff;
  border: 1px solid #000000;
  border-right: none;
  color: #000000;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}
.area-filter-btn:first-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.area-filter-btn:last-child {
  border-right: 1px solid #000000;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.area-filter-btn:hover {
  background: #f3f4f6;
}
.area-filter-btn.active {
  background: #000000;
  color: #ffffff;
}
.area-filter-count {
  background: #e5e7eb;
  padding: 1px 6px;
  border-radius: 0;
  font-size: 11px;
  font-weight: 600;
}
.area-filter-btn.active .area-filter-count {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}
.area-search-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 20px 12px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #000000;
  flex-shrink: 0;
}
.area-search-input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: #000000;
  font-size: 13px;
}
.area-search-input::placeholder {
  color: #9ca3af;
}
.area-search-clear {
  background: #000000;
  border: none;
  color: #ffffff;
  cursor: pointer;
  font-size: 11px;
  padding: 3px 7px;
}
.area-supplier-list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 20px 16px;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}
.area-supplier-list::-webkit-scrollbar {
  width: 6px;
}
.area-supplier-list::-webkit-scrollbar-track {
  background: transparent;
}
.area-supplier-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 0;
}
.area-supplier-row {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  margin-top: -1px;
  padding: 10px 16px;
  cursor: pointer;
}
.area-supplier-row:hover {
  border-color: #000000;
}
.area-row-inner {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}
.area-row-name {
  flex: 1;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.area-row-pct {
  flex-shrink: 0;
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.area-pct-unit {
  font-size: 12px;
  font-weight: 500;
  margin-left: 1px;
}
.area-supplier-row.area-ok .area-row-pct {
  color: #16a34a;
}
.area-supplier-row.area-warn .area-row-pct {
  color: #d97706;
}
.area-supplier-row.area-danger .area-row-pct {
  color: #dc2626;
}
.area-row-meta {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
  gap: 16px;
}
.area-meta-rent {
  width: 90px;
  flex-shrink: 0;
  text-align: left;
}
.area-meta-occupy {
  width: 100px;
  flex-shrink: 0;
  text-align: left;
}
.area-meta-bar {
  flex: 1;
  min-width: 0;
}
.area-bar-track {
  display: block;
  height: 8px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.area-bar-fill {
  height: 100%;
  display: block;
}
.area-supplier-row.area-ok .area-bar-fill {
  background: #16a34a;
}
.area-supplier-row.area-warn .area-bar-fill {
  background: #d97706;
}
.area-supplier-row.area-danger .area-bar-fill {
  background: #dc2626;
}
.area-ok {
  color: #22c55e;
}
.area-warn {
  color: #f59e0b;
}
.area-danger {
  color: #ef4444;
}
.area-empty {
  color: #64748b;
  text-align: center;
  padding: 30px 0;
  font-size: 13px;
}

.toast-msg {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  animation: toastIn 0.3s ease;
}

.toast-success {
  background: #065f46;
  color: #d1fae5;
  border: 1px solid #10b981;
}

.toast-error {
  background: #991b1b;
  color: #fee2e2;
  border: 1px solid #ef4444;
}

@keyframes toastIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.mobile-search-tabs {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #000000;
}

.mobile-tab-btn {
  flex: 1;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.mobile-tab-btn.active {
  background: #000000;
  border-color: #000000;
  color: #ffffff;
}

.mobile-supplier-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #d1d5db;
}

.mobile-supplier-label {
  color: #374151;
  font-size: 13px;
}

.mobile-supplier-name {
  color: #000000;
  font-size: 14px;
  font-weight: 600;
}

.mobile-material-item {
  text-align: left;
}

.mobile-material-item .result-title {
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.mobile-material-item .result-sub {
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.mobile-highlight-all {
  display: flex;
  gap: 8px;
  padding: 8px 0;
  margin-bottom: 8px;
}

.mobile-highlight-btn {
  flex: 1;
  padding: 10px;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  color: #000000;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.mobile-highlight-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mobile-cancel-btn {
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 0;
  color: #000000;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

@media (max-width: 768px) {
  .top-bar {
    padding: 0 10px;
    height: 44px;
    gap: 8px;
  }

  .tb-left {
    flex: 0 0 auto;
  }

  .tb-center {
    flex: 1;
    min-width: 0;
  }

  .logo-title {
    font-size: 14px;
    display: none;
  }

  .ware-select {
    width: 100%;
    max-width: 140px;
    font-size: 13px;
  }

  .search-trigger {
    display: none;
  }

  .tb-right {
    flex: 0 0 auto;
  }

  .bottom-drawer {
    display: none;
  }

  .float-zoom {
    top: auto;
    bottom: 16px;
    right: 10px;
  }

  .float-batch {
    display: none;
  }

  .warehouse-app {
    flex-direction: column;
  }

  .canvas-area {
    position: fixed;
    top: 50px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }

  .warehouse-viewport {
    position: absolute;
    top: 50%;
    left: 50%;
    width: calc(100vh - 50px);
    height: 100vw;
    transform: translate(-50%, -50%) rotate(90deg);
    touch-action: pan-x pan-y;
    -webkit-overflow-scrolling: touch;
    overflow: auto;
    will-change: transform;
  }

  .location {
    touch-action: pan-x pan-y;
  }

  .dialog-box {
    width: calc(100vw - 16px);
    max-height: 85vh;
    margin: auto 8px;
  }

  .dialog-header {
    padding: 16px 18px;
  }

  .dialog-body {
    padding: 12px 16px;
  }

  .material-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .material-stock {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
    padding-top: 8px;
    border-top: 1px solid rgba(59, 130, 246, 0.1);
  }
}
</style>

<style>
body {
  margin: 0;
  padding: 0;
}

/* ── 供应商历史占用趋势弹窗 ── */
.supplier-trend-modal {
  /* 宽屏布局：按屏幕比例设置，不受 .modal-box max-width:360px 限制 */
  box-sizing: border-box !important;
  width: min(1100px, 94vw) !important;
  min-width: 800px !important;
  max-width: 94vw !important;
  max-height: 88vh;
  text-align: left;
  display: flex;
  flex-direction: column;
  padding: 24px 28px !important;
}
.supplier-trend-modal .modal-box-title {
  text-align: center;
  border-bottom: 1px solid #000000;
  padding-bottom: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 16px;
}
.trend-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border: 1px solid #000000;
  background: #fafafa;
}
.trend-date-label {
  font-size: 12px;
  color: #374151;
  white-space: nowrap;
}
.trend-date-input {
  border: 1px solid #000000;
  border-radius: 0;
  padding: 5px 8px;
  font-size: 12px;
  background: #ffffff;
  color: #000000;
  outline: none;
  min-width: 130px;
}
.trend-date-input:focus {
  border-color: #1d4ed8;
}
.trend-query-btn {
  margin-left: auto;
  padding: 6px 22px;
  font-size: 12px;
}
.trend-chart-wrap {
  position: relative;
  width: 100%;
  height: 560px;
  min-height: 520px;
  border: 1px solid #000000;
  background: #ffffff;
  flex: 1 1 auto;
  overflow: hidden;
}
.trend-charts-stack {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}
.trend-chart-block {
  flex: 1 1 50%;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #e5e7eb;
}
.trend-chart-block:last-child {
  border-bottom: none;
}
.trend-chart-title {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  padding: 8px 12px 4px;
  background: #fafafa;
  border-bottom: 1px solid #e5e7eb;
}
.trend-chart-inner {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
}
.trend-loading,
.trend-error,
.trend-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #374151;
}
.trend-error {
  color: #dc2626;
}
.trend-stats {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  padding: 10px 14px;
  border: 1px solid #000000;
  background: #fafafa;
  font-size: 12px;
  color: #374151;
}
.trend-stats-row {
  display: flex;
  align-items: center;
  gap: 14px;
}
.trend-stats-tag {
  display: inline-block;
  padding: 2px 8px;
  border: 1px solid currentColor;
  font-size: 11px;
  font-weight: 600;
  min-width: 64px;
  text-align: center;
}
.tag-rate {
  color: #dc2626;
}
.tag-rent {
  color: #1d4ed8;
}
.tag-occupy {
  color: #16a34a;
}
.num-rate {
  color: #dc2626;
  font-weight: 600;
}
.num-rent {
  color: #1d4ed8;
  font-weight: 600;
}
.num-occupy {
  color: #16a34a;
  font-weight: 600;
}

/* ── 底部概况栏 ── */
.bottom-summary-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 56px;
  min-height: 56px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 0;
  z-index: 9999;
  box-sizing: border-box;
  user-select: none;
}
.summary-project {
  font-size: 13px;
  font-weight: 600;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}
.summary-divider {
  width: 1px;
  height: 20px;
  background: #d1d5db;
  margin: 0 12px;
  flex-shrink: 0;
}
.summary-item {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}
.summary-label {
  font-size: 12px;
  color: #6b7280;
}
.summary-value {
  font-size: 13px;
  font-weight: 600;
  color: #000000;
}
/* 占用率警示颜色 */
.summary-rate.rate-normal .summary-value {
  color: #16a34a;
}
.summary-rate.rate-warn .summary-value {
  color: #ea580c;
}
.summary-rate.rate-danger .summary-value {
  color: #dc2626;
  font-weight: 700;
}
.summary-rate.rate-danger {
  border: 1px solid #dc2626;
  border-radius: 4px;
  padding: 0 6px;
  margin: 0 -2px;
}
.summary-rate.rate-empty .summary-value {
  color: #9ca3af;
}
/* 热力图图例 */
.summary-legend {
  display: flex;
  align-items: flex-end; /* 色块与文字底部对齐 */
  gap: 4px;
  margin-left: auto;
  flex-shrink: 0;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: grab;
  touch-action: none; /* 防止指针事件被滚动抢占 */
}
.summary-legend.is-dragging {
  cursor: grabbing;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
}
.summary-legend.is-back {
  /* 返航中：配合 inline style 的 transition */
}
.legend-title {
  font-size: 11px;
  color: #6b7280;
  margin-right: 4px;
  white-space: nowrap;
  align-self: center;
}
.legend-swatch-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  line-height: 1;
}
.legend-swatch {
  width: 14px;
  height: 16px;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
}
.legend-swatch-label {
  font-size: 10px;
  color: #4b5563;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.legend-text {
  font-size: 11px;
  color: #6b7280;
  margin-left: 4px;
  white-space: nowrap;
  align-self: center;
}
@media (max-width: 600px) {
  .canvas-area {
    /* 顶部栏 50px + 手机端不显示底部概况栏 */
    position: fixed;
    top: 50px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }
  .bottom-summary-bar {
    display: none !important;
  }
  .summary-project {
    max-width: 90px;
    font-size: 12px;
  }
  .summary-divider {
    margin: 0 8px;
  }
  .legend-swatch {
    width: 10px;
    height: 12px;
  }
  .legend-title,
  .legend-text {
    display: none;
  }
}
</style>
